import { randomUUID } from 'node:crypto';
import type { Agent } from '../agent';
import { agentThreadStreamRuntime } from '../agent/thread-stream-runtime';
import type { DurableAgentLike } from '../agent/types';
import { isDurableAgentLike } from '../agent/types';
import { BackgroundTaskManager } from '../background-tasks';
import type { BackgroundTaskManagerConfig } from '../background-tasks/types';
import type { BundlerConfig } from '../bundler/types';
import { InMemoryServerCache } from '../cache';
import type { MastraServerCache } from '../cache';
import { AgentChannels } from '../channels';
import type { ChannelProvider } from '../channels';
import { DatasetsManager } from '../datasets/manager.js';
import type { MastraDeployer } from '../deployer';
import type { IMastraEditor } from '../editor';
import { MastraError, ErrorDomain, ErrorCategory } from '../error';
import type { MastraScorer } from '../evals';
import { EventEmitterPubSub } from '../events/event-emitter';
import type { PubSub } from '../events/pubsub';
import type { Event, EventCallback } from '../events/types';
import { AvailableHooks, registerHook } from '../hooks';
import type { MastraModelGateway } from '../llm/model/gateways';
import { defaultGateways } from '../llm/model/router';
import { LogLevel, noopLogger, ConsoleLogger, DualLogger } from '../logger';
import type { IMastraLogger } from '../logger';
import type { MCPServerBase } from '../mcp';
import type { MastraMemory } from '../memory';
import type { NotificationDispatchConfig } from '../notifications/workflow';
import { createNotificationDispatchWorkflow } from '../notifications/workflow';
import type {
  DefinitionSource,
  ObservabilityEntrypoint,
  ObservabilityExporter,
  ObservabilityInstance,
  LoggerContext,
  MetricsContext,
} from '../observability';
import { NoOpObservability, noOpLoggerContext, noOpMetricsContext } from '../observability';
import { initContextStorage } from '../observability/context-storage';
import type { Processor } from '../processors';
import type { MastraServerBase } from '../server/base';
import type { ApiRoute, Middleware, ServerConfig } from '../server/types';
import type { MastraCompositeStore, WorkflowRuns } from '../storage';
import type { Schedule, ScheduleUpdate, SchedulesStorage } from '../storage/domains/schedules/base';
import { augmentWithInit } from '../storage/storageWithInit';
import type { StorageResolvedPromptBlockType } from '../storage/types';
import type { ToolLoopAgentLike } from '../tool-loop-agent';
import { isToolLoopAgentLike, toolLoopAgentToMastraAgent } from '../tool-loop-agent';
import type { ToolAction, ToolPayloadTransformPolicy } from '../tools';
import { normalizeToolPayloadTransformPolicy } from '../tools/payload-transform';
import type { MastraTTS } from '../tts';
import type { MastraIdGenerator, IdGeneratorContext } from '../types';
import type { MastraVector } from '../vector';
import { OrchestrationWorker, SchedulerWorker, BackgroundTaskWorker } from '../worker';
import type { MastraWorker, WorkerDeps } from '../worker';
import type { AnyWorkflow, Workflow } from '../workflows';
import { WorkflowEventProcessor } from '../workflows/evented/workflow-event-processor';
import { computeNextFireAt } from '../workflows/scheduler';
import type { WorkflowScheduleConfig, WorkflowSchedulerConfig, WorkflowScheduler } from '../workflows/scheduler';
import type { AnyWorkspace, RegisteredWorkspace, Workspace } from '../workspace';
import { createOnScorerHook } from './hooks';
import type { VersionOverrides, VersionSelector } from './types';

/**
 * Creates an error for when a null/undefined value is passed to an add* method.
 * This commonly occurs when config is spread ({ ...config }) and the original
 * object had getters or non-enumerable properties.
 */
function createUndefinedPrimitiveError(
  type:
    | 'agent'
    | 'tool'
    | 'processor'
    | 'vector'
    | 'scorer'
    | 'workflow'
    | 'mcp-server'
    | 'gateway'
    | 'memory'
    | 'workspace',
  value: null | undefined,
  key?: string,
): MastraError {
  const typeLabel = type === 'mcp-server' ? 'MCP server' : type;
  const errorId = `MASTRA_ADD_${type.toUpperCase().replace('-', '_')}_UNDEFINED` as Uppercase<string>;
  return new MastraError({
    id: errorId,
    domain: ErrorDomain.MASTRA,
    category: ErrorCategory.USER,
    text: `Cannot add ${typeLabel}: ${typeLabel} is ${value === null ? 'null' : 'undefined'}. This may occur if config was spread ({ ...config }) and the original object had getters or non-enumerable properties.`,
    details: { status: 400, ...(key && { key }) },
  });
}

/**
 * Stable JSON-shape comparison for two `Schedule.target` values. Uses
 * JSON.stringify because targets are plain JSON-serializable objects (the
 * storage layer round-trips them through the same encoding). Covers the
 * `inputData` / `initialState` / `requestContext` payload fields that we
 * want to detect changes on across redeploys.
 */
function targetsEqual(a: Schedule['target'] | undefined, b: Schedule['target']): boolean {
  if (a === b) return true;
  if (!a) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Reads the declarative schedule configs off a workflow. Supports both the
 * new `getScheduleConfigs(): WorkflowScheduleConfig[]` accessor on the evented
 * engine and a legacy `getScheduleConfig(): WorkflowScheduleConfig | undefined`
 * fallback used in tests that inject a fake getter.
 */
function collectWorkflowScheduleConfigs(workflow: unknown): WorkflowScheduleConfig[] {
  const w = workflow as {
    getScheduleConfigs?: () => WorkflowScheduleConfig[] | undefined;
    getScheduleConfig?: () => WorkflowScheduleConfig | WorkflowScheduleConfig[] | undefined;
  };
  if (typeof w.getScheduleConfigs === 'function') {
    return w.getScheduleConfigs() ?? [];
  }
  if (typeof w.getScheduleConfig === 'function') {
    const cfg = w.getScheduleConfig();
    if (!cfg) return [];
    return Array.isArray(cfg) ? cfg : [cfg];
  }
  return [];
}

/**
 * Builds the storage row id for a declarative schedule. Workflow and schedule
 * ids are URL-encoded so delimiters in user-supplied ids cannot collide
 * across workflows (e.g. `foo__bar` single vs `foo` array-entry `bar`).
 */
function declarativeScheduleRowId(workflowId: string, scheduleId?: string): string {
  const encodedWorkflow = encodeURIComponent(workflowId);
  if (scheduleId === undefined) return `wf_${encodedWorkflow}`;
  return `wf_${encodedWorkflow}__${encodeURIComponent(scheduleId)}`;
}

/**
 * Determines whether a stored schedule row id belongs to one of the registered
 * workflows. Returns the owning workflow id when the row id either equals
 * `wf_<encoded(workflowId)>` (single-schedule form) or starts with
 * `wf_<encoded(workflowId)>__` (array form). Returns undefined when no
 * registered workflow owns the row.
 */
function ownerWorkflowIdForRow(rowId: string, byWorkflow: Map<string, Set<string>>): string | undefined {
  for (const workflowId of byWorkflow.keys()) {
    const prefix = `wf_${encodeURIComponent(workflowId)}`;
    if (rowId === prefix || rowId.startsWith(`${prefix}__`)) {
      return workflowId;
    }
  }
  return undefined;
}

/**
 * Decodes the owning workflow id directly from a `wf_<encoded>` /
 * `wf_<encoded>__<...>` row id without needing the workflow to be in the
 * current registry. Used to identify rows whose workflow has been deleted
 * from code so we can clean them up on startup.
 */
function ownerWorkflowIdFromRowId(rowId: string): string | undefined {
  if (!rowId.startsWith('wf_')) return undefined;
  const rest = rowId.slice('wf_'.length);
  const sep = rest.indexOf('__');
  const encoded = sep === -1 ? rest : rest.slice(0, sep);
  if (!encoded) return undefined;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return undefined;
  }
}

/** See {@link targetsEqual}. Same approach for free-form metadata. */
function metadataEqual(a: Record<string, unknown> | null | undefined, b: Record<string, unknown> | undefined): boolean {
  const aNorm = a ?? undefined;
  const bNorm = b ?? undefined;
  if (aNorm === bNorm) return true;
  if (!aNorm || !bNorm) return false;
  return JSON.stringify(aNorm) === JSON.stringify(bNorm);
}

/**
 * Configuration interface for initializing a Mastra instance.
 *
 * The Config interface defines all the optional components that can be registered
 * with a Mastra instance, including agents, workflows, storage, logging, and more.
 *
 * @template TAgents - Record of agent instances keyed by their names
 * @template TWorkflows - Record of workflow instances
 * @template TVectors - Record of vector store instances
 * @template TTTS - Record of text-to-speech instances
 * @template TLogger - Logger implementation type
 * @template TVNextNetworks - Record of agent network instances
 * @template TMCPServers - Record of MCP server instances
 * @template TScorers - Record of scorer instances
 *
 * @example
 * ```typescript
 * const mastra = new Mastra({
 *   agents: {
 *     weatherAgent: new Agent({
 *       id: 'weather-agent',
 *       name: 'Weather Agent',
 *       instructions: 'You help with weather information',
 *       model: 'openai/gpt-5'
 *     })
 *   },
 *   storage: new LibSQLStore({ id: 'mastra-storage', url: ':memory:' }),
 *   logger: new PinoLogger({ name: 'MyApp' })
 * });
 * ```
 */
export interface Config<
  TAgents extends Record<string, Agent<any>> = Record<string, Agent<any>>,
  TWorkflows extends Record<string, AnyWorkflow> = Record<string, AnyWorkflow>,
  TVectors extends Record<string, MastraVector<any>> = Record<string, MastraVector<any>>,
  TTTS extends Record<string, MastraTTS> = Record<string, MastraTTS>,
  TLogger extends IMastraLogger = IMastraLogger,
  TMCPServers extends Record<string, MCPServerBase<any>> = Record<string, MCPServerBase<any>>,
  TScorers extends Record<string, MastraScorer<any, any, any, any>> = Record<string, MastraScorer<any, any, any, any>>,
  TTools extends Record<string, ToolAction<any, any, any, any, any, any>> = Record<
    string,
    ToolAction<any, any, any, any, any, any>
  >,
  TProcessors extends Record<string, Processor<any>> = Record<string, Processor<any>>,
  TMemory extends Record<string, MastraMemory> = Record<string, MastraMemory>,
  TChannels extends Record<string, ChannelProvider> = Record<string, ChannelProvider>,
> {
  /**
   * Agents are autonomous systems that can make decisions and take actions.
   * Accepts Mastra Agent instances, AI SDK v6 ToolLoopAgent instances,
   * and durable agent wrappers (e.g., InngestAgent from createInngestAgent).
   * ToolLoopAgent and durable agents are automatically handled during registration.
   */
  agents?: { [K in keyof TAgents]: TAgents[K] | ToolLoopAgentLike | DurableAgentLike };

  /**
   * Storage provider for persisting data, conversation history, and workflow state.
   * Required for agent memory and workflow persistence.
   */
  storage?: MastraCompositeStore;

  /**
   * Vector stores for semantic search and retrieval-augmented generation (RAG).
   * Used for storing and querying embeddings.
   */
  vectors?: TVectors;

  /**
   * Logger implementation for application logging and debugging.
   * Set to `false` to disable logging entirely.
   * @default `INFO` level in development, `WARN` in production.
   */
  logger?: TLogger | false;

  /**
   * Workflows provide type-safe, composable task execution with built-in error handling.
   */
  workflows?: TWorkflows;

  /**
   * Text-to-speech providers for voice synthesis capabilities.
   */
  tts?: TTTS;

  /**
   * Observability entrypoint for tracking model interactions and tracing.
   * Pass an instance of the Observability class from @mastra/observability.
   *
   * @example
   * ```typescript
   * import { Observability, MastraStorageExporter, MastraPlatformExporter } from '@mastra/observability';
   *
   * new Mastra({
   *   observability: new Observability({
   *     configs: {
   *       default: {
   *         serviceName: 'mastra',
   *         exporters: [new MastraStorageExporter(), new MastraPlatformExporter()],
   *       },
   *     },
   *   })
   * })
   * ```
   *
   * `Observability` auto-applies a `SensitiveDataFilter` span output processor
   * to every configured instance. Set `sensitiveDataFilter: false` on the
   * registry config to opt out, or pass a `SensitiveDataFilterOptions` object
   * to customize it.
   */
  observability?: ObservabilityEntrypoint;

  /**
   * Custom ID generator function for creating unique identifiers.
   * Receives optional context about what type of ID is being generated
   * and where it's being requested from.
   * @default `crypto.randomUUID()`
   */
  idGenerator?: MastraIdGenerator;

  /**
   * Deployment provider for publishing applications to cloud platforms.
   */
  deployer?: MastraDeployer;

  /**
   * Server configuration for HTTP endpoints and middleware.
   */
  server?: ServerConfig;

  /**
   * MCP servers provide tools and resources that agents can use.
   */
  mcpServers?: TMCPServers;

  /**
   * Bundler configuration for packaging and deployment.
   */
  bundler?: BundlerConfig;

  /**
   * Pub/sub system for event-driven communication between components.
   * @default EventEmitterPubSub
   */
  pubsub?: PubSub;

  /**
   * Server cache for storing stream events and other temporary data.
   * Used by durable agents for resumable streams - clients can disconnect
   * and reconnect without missing events.
   *
   * When provided, durable agents created without their own cache will
   * inherit this cache instance.
   *
   * @default InMemoryServerCache
   */
  cache?: MastraServerCache;

  /**
   * Scorers help assess the quality of agent responses and workflow outputs.
   */
  scorers?: TScorers;

  /**
   * Tools are reusable functions that agents can use to interact with external systems.
   */
  tools?: TTools;

  /**
   * Processors transform inputs and outputs for agents and workflows.
   */
  processors?: TProcessors;

  /**
   * Memory instances that can be referenced by stored agents.
   * Keys are used to look up memory instances when resolving stored agent configurations.
   */
  memory?: TMemory;

  /**
   * Global workspace for file storage, skills, and code execution.
   * Agents inherit this workspace unless they have their own configured.
   * Skills are accessed via workspace.skills when skills is configured.
   */
  workspace?: AnyWorkspace;

  /**
   * Custom model router gateways for accessing LLM providers.
   * Gateways handle provider-specific authentication, URL construction, and model resolution.
   */
  gateways?: Record<string, MastraModelGateway>;

  /**
   * Event handlers for custom application events.
   * Maps event topics to handler functions for event-driven architectures.
   */
  events?: {
    [topic: string]: (
      event: Event,
      cb?: () => Promise<void>,
    ) => Promise<void> | ((event: Event, cb?: () => Promise<void>) => Promise<void>)[];
  };

  /**
   * Editor instance for handling agent instantiation and configuration.
   * The editor handles complex instantiation logic including memory resolution.
   */
  editor?: IMastraEditor;

  /**
   * Global version overrides for primitives.
   * When set, sub-agent delegation (and future primitive resolution) will
   * resolve the specified version instead of the code-defined default.
   *
   * @example
   * ```typescript
   * new Mastra({
   *   versions: {
   *     agents: {
   *       'researcher-agent': { versionId: '123' },
   *       'writer-agent': { status: 'published' },
   *     },
   *   },
   * });
   * ```
   */
  versions?: VersionOverrides;

  /**
   * Background task configuration for running tool calls asynchronously.
   * When configured, agents can dispatch tool executions to run in the background
   * while the conversation continues.
   */
  backgroundTasks?: BackgroundTaskManagerConfig;

  /**
   * Scheduler configuration for cron-driven workflow triggers.
   *
   * The scheduler is auto-enabled when any registered workflow declares a
   * `schedule` config or when `scheduler.enabled` is true. It requires a
   * storage adapter implementing the `schedules` domain (e.g. `@mastra/libsql`).
   */
  scheduler?: WorkflowSchedulerConfig;

  /**
   * Notification runtime configuration. Notification dispatch is scheduled automatically by default.
   */
  notifications?: {
    dispatch?: NotificationDispatchConfig;
  };

  /**
   * Platform channels for messaging integrations (Slack, Discord, etc.).
   * Routes are automatically registered and agents can reference channel configs.
   *
   * @example
   * ```typescript
   * import { SlackProvider } from '@mastra/slack';
   *
   * new Mastra({
   *   channels: {
   *     slack: new SlackProvider({
   *       configToken: process.env.SLACK_APP_CONFIG_TOKEN,
   *       refreshToken: process.env.SLACK_APP_CONFIG_REFRESH_TOKEN,
   *     }),
   *   },
   * });
   * ```
   */
  channels?: TChannels;

  /**
   * Deployment environment name (e.g. `'production'`, `'staging'`, `'development'`).
   * When set, the value is automatically attached to all observability signals
   * so they can be filtered by environment without passing
   * `tracingOptions.metadata.environment` on every call.
   *
   * If unset, falls back to `process.env.NODE_ENV`. If neither is set the field
   * is left undefined rather than guessed.
   *
   * Per-call `tracingOptions.metadata.environment` always takes precedence.
   *
   * @example
   * ```typescript
   * new Mastra({
   *   environment: 'production',
   *   observability: new Observability({ ... }),
   * })
   * ```
   */
  environment?: string;
  /**
   * Optional central transform policy for tool payloads before they are
   * serialized into display streams or user-visible transcripts.
   */
  transform?: ToolPayloadTransformPolicy;
  /**
   * Configure which workers run in this Mastra instance.
   *
   * - `undefined` (default): Auto-creates default workers (existing behavior)
   * - `false`: Disables all event processing — useful when running standalone workers separately
   * - `MastraWorker[]`: Use exactly these workers
   */
  workers?: MastraWorker[] | false;
}

/**
 * The central orchestrator for Mastra applications, managing agents, workflows, storage, logging, observability, and more.
 *
 * The `Mastra` class serves as the main entry point and registry for all components in a Mastra application.
 * It coordinates the interaction between agents, workflows, storage systems, and other services.

 * @template TAgents - Record of agent instances keyed by their names
 * @template TWorkflows - Record of modern workflow instances
 * @template TVectors - Record of vector store instances for semantic search and RAG
 * @template TTTS - Record of text-to-speech provider instances
 * @template TLogger - Logger implementation type for application logging
 * @template TVNextNetworks - Record of next-generation agent network instances
 * @template TMCPServers - Record of Model Context Protocol server instances
 * @template TScorers - Record of evaluation scorer instances for measuring AI performance
 *
 * @example
 * ```typescript
 * const mastra = new Mastra({
 *   agents: {
 *     weatherAgent: new Agent({
 *       id: 'weather-agent',
 *       name: 'Weather Agent',
 *       instructions: 'You provide weather information',
 *       model: 'openai/gpt-5',
 *       tools: [getWeatherTool]
 *     })
 *   },
 *   workflows: { dataWorkflow },
 *   storage: new LibSQLStore({ id: 'mastra-storage', url: ':memory:' }),
 *   logger: new PinoLogger({ name: 'MyApp' })
 * });
 * ```
 */
export class Mastra<
  TAgents extends Record<string, Agent<any>> = Record<string, Agent<any>>,
  TWorkflows extends Record<string, AnyWorkflow> = Record<string, AnyWorkflow>,
  TVectors extends Record<string, MastraVector<any>> = Record<string, MastraVector<any>>,
  TTTS extends Record<string, MastraTTS> = Record<string, MastraTTS>,
  TLogger extends IMastraLogger = IMastraLogger,
  TMCPServers extends Record<string, MCPServerBase<any>> = Record<string, MCPServerBase<any>>,
  TScorers extends Record<string, MastraScorer<any, any, any, any>> = Record<string, MastraScorer<any, any, any, any>>,
  TTools extends Record<string, ToolAction<any, any, any, any, any, any>> = Record<
    string,
    ToolAction<any, any, any, any, any, any>
  >,
  TProcessors extends Record<string, Processor<any>> = Record<string, Processor<any>>,
  TMemory extends Record<string, MastraMemory> = Record<string, MastraMemory>,
  TChannels extends Record<string, ChannelProvider> = Record<string, ChannelProvider>,
> {
  #vectors?: TVectors;
  #agents: TAgents;
  #logger: TLogger;
  #workflows: TWorkflows;
  #hiddenWorkflowKeys = new Set<string>();
  #observability: ObservabilityEntrypoint;
  #tts?: TTTS;
  #deployer?: MastraDeployer;
  #serverMiddleware: Array<{
    handler: (c: any, next: () => Promise<void>) => Promise<Response | void>;
    path: string;
  }> = [];

  #storage?: MastraCompositeStore;
  #scorers?: TScorers;
  #tools?: TTools;
  #processors?: TProcessors;
  #processorConfigurations: Map<string, Array<{ processor: Processor; agentId: string; type: 'input' | 'output' }>> =
    new Map();
  #memory?: TMemory;
  #workspace?: Workspace;
  #workspaces: Record<string, RegisteredWorkspace> = {};
  #server?: ServerConfig;
  #serverAdapter?: MastraServerBase;
  #mcpServers?: TMCPServers;
  #bundler?: BundlerConfig;
  #idGenerator?: MastraIdGenerator;
  #pubsub: PubSub;
  #backgroundTaskConfig?: BackgroundTaskManagerConfig;
  #backgroundTaskManager?: BackgroundTaskManager;
  #schedulerConfig?: WorkflowSchedulerConfig;
  #notificationDispatchConfig?: NotificationDispatchConfig;
  /**
   * Tracks whether any registered workflow has declared a `schedule` config.
   * Used as a fast short-circuit so users without scheduled workflows pay
   * zero cost beyond a boolean check.
   */
  #hasScheduledWorkflow = false;
  #gateways?: Record<string, MastraModelGateway>;
  #channels?: TChannels;
  #environment?: string;
  #toolPayloadTransform?: ToolPayloadTransformPolicy;
  #workers: MastraWorker[] = [];
  #workerFilter?: Set<string>;
  // Lazily-constructed processor used by handleWorkflowEvent(). Shared between
  // pull-mode workers (OrchestrationWorker) and push-mode entry points
  // (in-process EventEmitter listener, the /api/workers/events HTTP route).
  #workflowEventProcessor?: WorkflowEventProcessor;
  // Callback registered against the pubsub when running in push mode so we can
  // unsubscribe it cleanly during stopWorkers().
  #pushSubscription?: { topic: string; cb: EventCallback };
  // Tracks (topic, listener) pairs registered against the pubsub on behalf of
  // user-defined event listeners during startWorkers(). Used to make
  // startWorkers()/stopWorkers() idempotent — a second startWorkers() call
  // must not double-subscribe the same listener.
  #userEventSubscriptions: Array<{
    topic: string;
    cb: (event: Event, ack?: () => Promise<void>) => Promise<void>;
  }> = [];

  #events: {
    [topic: string]: ((event: Event, cb?: () => Promise<void>) => Promise<void>)[];
  } = {};
  #internalMastraWorkflows: Record<string, Workflow> = {};
  // Server cache for temporary persistence and durable agent resumable streams
  #serverCache: MastraServerCache;
  // Cache for stored agents to allow in-memory modifications (like model changes) to persist across requests
  #storedAgentsCache: Map<string, Agent> = new Map();
  // Cache for stored scorers to allow in-memory modifications to persist across requests
  #storedScorersCache: Map<string, MastraScorer<any, any, any, any>> = new Map();
  // Registry for prompt blocks (stored or code-defined)
  #promptBlocks: Record<string, StorageResolvedPromptBlockType> = {};
  // Editor instance for handling agent instantiation and configuration
  #editor?: IMastraEditor;
  #datasets?: DatasetsManager;
  // Global version overrides for primitives (agents, etc.)
  #versions?: VersionOverrides;

  get pubsub() {
    return this.#pubsub;
  }

  get agentThreadStreamRuntime() {
    return agentThreadStreamRuntime;
  }

  get workers(): readonly MastraWorker[] {
    return this.#workers;
  }

  getWorker<T extends MastraWorker>(name: string): T | undefined {
    return this.#workers.find(w => w.name === name) as T | undefined;
  }

  get backgroundTaskManager() {
    return this.#backgroundTaskManager;
  }

  /**
   * Returns the workflow scheduler owned by the SchedulerWorker,
   * or undefined if the scheduler is not enabled / not yet started.
   *
   * The scheduler is created when `startWorkers()` initializes the
   * SchedulerWorker (guarded by `#shouldEnableScheduler()`). Use it
   * to create, pause, resume, or delete schedules imperatively.
   */
  get scheduler(): WorkflowScheduler | undefined {
    return this.#findSchedulerWorker()?.scheduler;
  }

  get datasets(): DatasetsManager {
    if (!this.#datasets) {
      this.#datasets = new DatasetsManager(this);
    }
    return this.#datasets;
  }

  /**
   * Gets the currently configured ID generator function.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   idGenerator: context =>
   *     context?.idType === 'message' && context.threadId
   *       ? `msg-${context.threadId}-${Date.now()}`
   *       : `custom-${Date.now()}`
   * });
   * const generator = mastra.getIdGenerator();
   * console.log(generator?.({ idType: 'message', threadId: 'thread-123' })); // \"msg-thread-123-1234567890\"
   * ```
   */
  public getIdGenerator() {
    return this.#idGenerator;
  }

  /**
   * Gets the currently configured editor instance.
   * The editor is responsible for handling agent instantiation and configuration.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   editor: new MastraEditor({ logger })
   * });
   * const editor = mastra.getEditor();
   * ```
   */
  public getEditor() {
    return this.#editor;
  }

  /**
   * Gets a registered channel provider by its key.
   *
   * @example
   * ```typescript
   * import { SlackProvider } from '@mastra/slack';
   * const slack = mastra.getChannelProvider<SlackProvider>('slack');
   * ```
   */
  public getChannelProvider<T extends ChannelProvider = ChannelProvider>(key: string): T | undefined {
    return this.#channels?.[key] as T | undefined;
  }

  /**
   * Gets all registered channel providers.
   */
  public getChannelProviders(): Record<string, ChannelProvider> | undefined {
    return this.#channels;
  }

  /**
   * Shorthand getter for platform channels.
   * Usage: `mastra.channels.slack.connect(agentId)`
   */
  public get channels(): TChannels {
    return (this.#channels ?? {}) as TChannels;
  }

  /**
   * Returns the global version overrides configured on this Mastra instance.
   * These are used as defaults when resolving sub-agent versions during delegation.
   */
  public getVersionOverrides(): VersionOverrides | undefined {
    return this.#versions;
  }

  /**
   * Returns the deployment environment name configured on this Mastra instance,
   * falling back to `process.env.NODE_ENV` when unset, or `undefined` if neither
   * is provided.
   *
   * Observability automatically reads this and attaches it to all signals so
   * consumers can filter by environment without passing
   * `tracingOptions.metadata.environment` on each call.
   */
  public getEnvironment(): string | undefined {
    return this.#environment;
  }

  public getToolPayloadTransform(): ToolPayloadTransformPolicy | undefined {
    return this.#toolPayloadTransform;
  }

  /**
   * Gets the stored agents cache
   * @internal
   */
  public getStoredAgentCache() {
    return this.#storedAgentsCache;
  }

  /**
   * Gets the stored scorers cache
   * @internal
   */
  public getStoredScorerCache() {
    return this.#storedScorersCache;
  }

  /**
   * Generates a unique identifier using the configured generator or defaults to `crypto.randomUUID()`.
   *
   * This method is used internally by Mastra for creating unique IDs for various entities
   * like workflow runs, agent conversations, and other resources that need unique identification.
   *
   * @param context - Optional context information about what type of ID is being generated
   *                  and where it's being requested from. This allows custom ID generators
   *                  to create deterministic IDs based on context.
   *
   * @throws {MastraError} When the custom ID generator returns an empty string
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const id = mastra.generateId();
   * console.log(id); // "550e8400-e29b-41d4-a716-446655440000"
   *
   * // With context for deterministic IDs
   * const messageId = mastra.generateId({
   *   idType: 'message',
   *   source: 'agent',
   *   threadId: 'thread-123'
   * });
   * ```
   */
  public generateId(context?: IdGeneratorContext): string {
    if (this.#idGenerator) {
      const id = this.#idGenerator(context);
      if (!id) {
        const error = new MastraError({
          id: 'MASTRA_ID_GENERATOR_RETURNED_EMPTY_STRING',
          domain: ErrorDomain.MASTRA,
          category: ErrorCategory.USER,
          text: 'ID generator returned an empty string, which is not allowed',
        });
        this.#logger?.trackException(error);
        throw error;
      }
      return id;
    }
    return randomUUID();
  }

  /**
   * Sets a custom ID generator function for creating unique identifiers.
   *
   * The ID generator function will be used by `generateId()` instead of the default
   * `crypto.randomUUID()`. This is useful for creating application-specific ID formats
   * or integrating with existing ID generation systems. The function receives
   * optional context about what is requesting the ID.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * mastra.setIdGenerator(context =>
   *   context?.idType === 'run' && context.entityId
   *     ? `run-${context.entityId}-${Date.now()}`
   *     : `custom-${Date.now()}`
   * );
   * const id = mastra.generateId({ idType: 'run', entityId: 'agent-123' });
   * console.log(id); // "run-agent-123-1234567890"
   * ```
   */
  public setIdGenerator(idGenerator: MastraIdGenerator) {
    this.#idGenerator = idGenerator;
  }

  /**
   * Sets the server configuration for this Mastra instance.
   *
   * @param server - The server configuration object
   *
   * @example
   * ```typescript
   * mastra.setServer({ ...mastra.getServer(), auth: new MastraAuthWorkos() });
   * ```
   */
  public setServer(server: ServerConfig): void {
    this.#server = server;
  }

  /**
   * Registers an exporter on the default observability instance.
   *
   * If the current observability is a no-op (user didn't configure any), it is
   * first replaced with the provided entrypoint and the instance is registered
   * as default. If a real observability entrypoint already exists, the exporter
   * is added directly to the existing default instance.
   *
   * @param exporter - The exporter to register (e.g. a MastraPlatformExporter)
   * @param instance - An ObservabilityInstance pre-configured with the exporter, used as default when bootstrapping
   * @param entrypoint - A real ObservabilityEntrypoint to bootstrap if the current one is a no-op
   */
  public registerExporter(
    exporter: ObservabilityExporter,
    instance: ObservabilityInstance,
    entrypoint: ObservabilityEntrypoint,
  ): void {
    if (this.#observability instanceof NoOpObservability) {
      this.#observability = entrypoint;
      this.#observability.setLogger({ logger: this.#logger });
      this.#observability.setMastraContext({ mastra: this });
      this.#observability.registerInstance('default', instance, true);
    }

    const defaultInstance = this.#observability.getDefaultInstance();
    if (defaultInstance?.registerExporter) {
      defaultInstance.registerExporter(exporter);
    }
  }

  /**
   * Creates a new Mastra instance with the provided configuration.
   *
   * The constructor initializes all the components specified in the config, sets up
   * internal systems like logging and observability, and registers components with each other.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   agents: {
   *     assistant: new Agent({
   *       id: 'assistant',
   *       name: 'Assistant',
   *       instructions: 'You are a helpful assistant',
   *       model: 'openai/gpt-5'
   *     })
   *   },
   *   storage: new PostgresStore({
   *     connectionString: process.env.DATABASE_URL
   *   }),
   *   logger: new PinoLogger({ name: 'MyApp' }),
   *   observability: new Observability({
   *     configs: { default: { serviceName: 'mastra', exporters: [new MastraStorageExporter()] } },
   *   }),
   * });
   * ```
   */
  constructor(
    config?: Config<
      TAgents,
      TWorkflows,
      TVectors,
      TTTS,
      TLogger,
      TMCPServers,
      TScorers,
      TTools,
      TProcessors,
      TMemory,
      TChannels
    >,
  ) {
    // Register AsyncLocalStorage-backed context resolvers so that DualLogger
    // can correlate logs to the active span. Must happen before any agent runs.
    initContextStorage();

    // Server cache for temporary persistence and durable agent resumable streams
    this.#serverCache = config?.cache ?? new InMemoryServerCache();

    this.#editor = config?.editor;

    // Store global version overrides
    this.#versions = config?.versions;

    // Resolve deployment environment: explicit config wins, else fall back to
    // NODE_ENV. Leave undefined if neither is set rather than guessing.
    this.#environment = config?.environment ?? process.env.NODE_ENV;
    this.#toolPayloadTransform = normalizeToolPayloadTransformPolicy(
      config?.transform ?? (config as any)?.toolPayloadProjection,
    );

    if (config?.pubsub) {
      this.#pubsub = config.pubsub;
    } else {
      this.#pubsub = new EventEmitterPubSub();
    }

    this.#events = {};
    for (const topic in config?.events ?? {}) {
      if (!Array.isArray(config?.events?.[topic])) {
        this.#events[topic] = [config?.events?.[topic] as any];
      } else {
        this.#events[topic] = config?.events?.[topic] ?? [];
      }
    }

    // Initialize workers based on config.
    // MASTRA_WORKERS env var:
    //   - "false": disables all event processing in this instance
    //   - comma-separated names (e.g. "scheduler,orchestration"): only those
    //     workers will be started by `startWorkers()` when called without an
    //     explicit `name` argument. Construction still creates all workers so
    //     a later explicit `startWorkers('foo')` still works.
    const rawWorkersEnv = process.env.MASTRA_WORKERS;
    let workersOption: MastraWorker[] | false | undefined;
    if (rawWorkersEnv === 'false') {
      workersOption = false;
    } else {
      workersOption = config?.workers;
      if (rawWorkersEnv && rawWorkersEnv !== 'false') {
        const names = rawWorkersEnv
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
        if (names.length > 0) {
          this.#workerFilter = new Set(names);
        }
      }
    }

    if (workersOption === false) {
      // Explicitly disabled — no event processing in this instance.
      // PubSub still exists for publishing events.
    } else if (Array.isArray(workersOption)) {
      this.#workers = workersOption;
      for (const w of this.#workers) {
        w.__registerMastra(this);
      }
    } else {
      // Default: auto-create workers based on config.
      //
      // Skip OrchestrationWorker when the configured pubsub doesn't support
      // pull delivery (e.g. EventEmitter, GCP Pub/Sub push) — those transports
      // don't have a read loop to drive a worker, and Mastra wires
      // `handleWorkflowEvent` directly to the pubsub during startWorkers().
      const pubsubModes = this.#pubsub.supportedModes ?? ['pull'];
      const defaultWorkers: MastraWorker[] = [];
      if (pubsubModes.includes('pull')) {
        defaultWorkers.push(new OrchestrationWorker());
      }
      // SchedulerWorker is added lazily in startWorkers() rather than here
      // because workflows (and their schedule configs) are registered after
      // this block runs, so #hasScheduledWorkflow is not yet set.
      if (config?.backgroundTasks?.enabled) {
        defaultWorkers.push(new BackgroundTaskWorker(config.backgroundTasks));
      }
      this.#workers = defaultWorkers;
      for (const w of this.#workers) {
        w.__registerMastra(this);
      }
    }

    let logger: TLogger;
    if (config?.logger === false) {
      logger = noopLogger as unknown as TLogger;
    } else {
      if (config?.logger) {
        logger = config.logger;
      } else {
        const levelOnEnv =
          process.env.NODE_ENV === 'production' && process.env.MASTRA_DEV !== 'true' ? LogLevel.WARN : LogLevel.INFO;
        logger = new ConsoleLogger({ name: 'Mastra', level: levelOnEnv }) as unknown as TLogger;
      }
    }
    this.#logger = logger;

    this.#idGenerator = config?.idGenerator;

    let storage = config?.storage;

    if (storage) {
      storage = augmentWithInit(storage);
    }

    // Validate and assign observability instance
    if (config?.observability) {
      if (typeof config.observability.getDefaultInstance === 'function') {
        this.#observability = config.observability;
        // Set logger early
        this.#observability.setLogger({ logger: this.#logger });
      } else {
        this.#logger?.warn(
          'Observability configuration error: Expected an Observability instance, but received a config object. ' +
            'Import and instantiate: import { Observability, MastraStorageExporter } from "@mastra/observability"; ' +
            'then pass: observability: new Observability({ configs: { default: { serviceName: "mastra", exporters: [new MastraStorageExporter()] } } }). ' +
            'Observability has been disabled.',
        );
        this.#observability = new NoOpObservability();
      }
    } else {
      this.#observability = new NoOpObservability();
    }

    // Wrap the logger in a DualLogger so all existing this.logger.info(...) calls
    // also forward to loggerVNext (observability structured logging).
    // This is transparent — no call sites need to change.
    // Uses a lazy getter so loggerVNext is always resolved at call time
    // (observability may not be fully initialized yet at this point).
    const dualLogger = new DualLogger(this.#logger, () => this.loggerVNext);
    this.#logger = dualLogger as unknown as TLogger;

    this.#storage = storage;

    // Give storage adapters a back-pointer to this Mastra instance so they
    // can look up code-defined agents, editor config, etc. when needed
    // (e.g. filesystem code-mode snapshot filtering).
    storage?.__registerMastra?.(this as unknown as Parameters<NonNullable<typeof storage.__registerMastra>>[0]);

    // Register the editor after storage is assigned so code mode can overlay
    // filesystem-backed editor storage while preserving app storage domains.
    if (this.#editor && typeof this.#editor.registerWithMastra === 'function') {
      this.#editor.registerWithMastra(this);
    }

    this.#backgroundTaskConfig = config?.backgroundTasks;
    // Auto-create the background-task manager only when this Mastra is
    // running workers. When `workers: false`, the consumer of the
    // background-tasks topic must live elsewhere — the producer can still
    // construct its own `BackgroundTaskManager` and call `init()` directly
    // (see redis-streams cross-process tests for that pattern). Initializing
    // a worker here would compete with the dedicated worker process for
    // dispatch events.
    if (workersOption !== false) {
      this.#ensureBackgroundTaskManager();
    }

    this.#schedulerConfig = config?.scheduler;
    this.#notificationDispatchConfig = config?.notifications?.dispatch;

    // Initialize all primitive storage objects first, we need to do this before adding primitives to avoid circular dependencies
    this.#vectors = {} as TVectors;
    this.#mcpServers = {} as TMCPServers;
    this.#tts = {} as TTTS;
    this.#agents = {} as TAgents;
    this.#scorers = {} as TScorers;
    this.#tools = {} as TTools;
    this.#processors = {} as TProcessors;
    this.#memory = {} as TMemory;
    this.#workflows = {} as TWorkflows;
    this.#gateways = {} as Record<string, MastraModelGateway>;

    // Now add primitives - order matters for auto-registration
    // Tools and processors should be added before agents and MCP servers that might use them
    // Note: We validate each entry to handle cases where config was spread ({ ...config })
    // which can cause undefined values if the source object had getters or non-enumerable properties
    if (config?.tools) {
      Object.entries(config.tools).forEach(([key, tool]) => {
        if (tool != null) {
          this.addTool(tool, key);
        }
      });
    }

    if (config?.processors) {
      Object.entries(config.processors).forEach(([key, processor]) => {
        if (processor != null) {
          this.addProcessor(processor, key);
        }
      });
    }

    if (config?.memory) {
      Object.entries(config.memory).forEach(([key, memory]) => {
        if (memory != null) {
          this.addMemory(memory, key);
        }
      });
    }

    if (config?.vectors) {
      Object.entries(config.vectors).forEach(([key, vector]) => {
        if (vector != null) {
          this.addVector(vector, key);
        }
      });
    }

    if (config?.workspace) {
      this.#workspace = config.workspace;
      // Also register in the workspaces registry for direct lookup by ID
      this.addWorkspace(config.workspace, undefined, { source: 'mastra' });
    }

    if (config?.scorers) {
      Object.entries(config.scorers).forEach(([key, scorer]) => {
        if (scorer != null) {
          this.addScorer(scorer, key, { source: 'code' });
        }
      });
    }

    if (this.#notificationDispatchConfig?.enabled !== false) {
      const workflow = createNotificationDispatchWorkflow(this.#notificationDispatchConfig);
      this.addWorkflow(workflow, workflow.id);
      this.#hiddenWorkflowKeys.add(workflow.id);
    }

    if (config?.workflows) {
      Object.entries(config.workflows).forEach(([key, workflow]) => {
        if (workflow != null) {
          this.addWorkflow(workflow, key);
        }
      });
    }

    if (config?.gateways) {
      Object.entries(config.gateways).forEach(([key, gateway]) => {
        if (gateway != null) {
          this.addGateway(gateway, key);
        }
      });
    }

    // Auto-register default gateways (MastraGateway, NetlifyGateway, ModelsDevGateway)
    // so they're available via listGateways() without explicit config.
    // Skip duplicates so user-provided gateways above take precedence.
    // Added directly to #gateways to avoid triggering #syncGatewayRegistry for built-ins.
    for (const gateway of defaultGateways) {
      const key = gateway.getId();
      if (!(this.#gateways as Record<string, MastraModelGateway>)[key]) {
        (this.#gateways as Record<string, MastraModelGateway>)[key] = gateway;
      }
    }

    // Add MCP servers and agents last since they might reference other primitives
    if (config?.mcpServers) {
      Object.entries(config.mcpServers).forEach(([key, server]) => {
        if (server != null) {
          this.addMCPServer(server, key);
        }
      });
    }

    if (config?.tts) {
      Object.entries(config.tts).forEach(([key, tts]) => {
        if (tts != null) {
          (this.#tts as Record<string, MastraTTS>)[key] = tts;
        }
      });
    }

    if (config?.server) {
      this.#server = config.server;
    }

    // Register channels and merge their routes into server config
    if (config?.channels) {
      this.#channels = config.channels;
      const channelRoutes: ApiRoute[] = [];

      for (const [, channel] of Object.entries(config.channels)) {
        if (channel == null) continue;

        // Attach the channel to this Mastra instance
        if (channel.__attach) {
          channel.__attach(this);
        }

        // Collect routes from the channel
        const routes = channel.getRoutes();
        channelRoutes.push(...routes);
      }

      // Merge channel routes into server config
      if (channelRoutes.length > 0) {
        const existingRoutes = this.#server?.apiRoutes ?? [];
        this.#server = {
          ...this.#server,
          apiRoutes: [...existingRoutes, ...channelRoutes],
        };
      }
    }

    // Agents must be added after server config so that channel webhook routes
    // are appended to (not replaced by) the server config.
    if (config?.agents) {
      Object.entries(config.agents).forEach(([key, agent]) => {
        if (agent != null) {
          this.addAgent(agent, key);
        }
      });
    }

    registerHook(AvailableHooks.ON_SCORER_RUN, createOnScorerHook(this));

    /*
      Initialize observability with Mastra context (after storage configured)
    */
    this.#observability.setMastraContext({ mastra: this });

    this.setLogger({ logger });

    // Initialize channels asynchronously (auto-provision apps, etc.)
    // This runs after all agents are registered so configs are available
    if (this.#channels) {
      void Promise.resolve().then(async () => {
        for (const [key, channel] of Object.entries(this.#channels ?? {})) {
          if (channel.initialize) {
            try {
              await channel.initialize();
            } catch (err) {
              console.error(`[Mastra] Failed to initialize channel "${key}":`, err);
            }
          }
        }
      });
    }
  }

  #ensureBackgroundTaskManager(): void {
    if (!this.#backgroundTaskConfig?.enabled || !this.#storage || this.#backgroundTaskManager) {
      return;
    }

    const bgManager = new BackgroundTaskManager(this.#backgroundTaskConfig);
    bgManager.__registerMastra(this);
    this.#backgroundTaskManager = bgManager;

    // Wire statically-registered tools into the manager's name-keyed registry
    // so cross-process workers can resolve dispatched tasks. Tools added later
    // via `addTool()` are propagated through the same path.
    const tools = this.#tools as Record<string, ToolAction<any, any, any, any>> | undefined;
    if (tools) {
      for (const [name, tool] of Object.entries(tools)) {
        this.#registerToolWithBackgroundManager(name, tool);
      }
    }

    void bgManager.init(this.#pubsub).catch(error => {
      this.#logger?.error('Failed to initialize background task manager', error);
    });
  }

  /**
   * Build a `ToolExecutor` adapter for a Mastra-registered tool and stash it
   * on the background task manager's static registry. Skipped if the tool has
   * no `execute` (declarative-only tools, e.g. MCP descriptors).
   */
  #registerToolWithBackgroundManager(name: string, tool: ToolAction<any, any, any, any>): void {
    if (!this.#backgroundTaskManager) return;
    if (typeof tool.execute !== 'function') return;
    const execute = tool.execute.bind(tool);
    this.#backgroundTaskManager.registerStaticExecutor(name, {
      execute: async (args, options) => {
        // Cross-process workers don't have access to the producer's
        // request/workspace context. Statically-resolvable tools should
        // tolerate a minimal context (abortSignal only). Tools that need
        // closure-captured state must run in-process via TaskContext.
        return execute(
          args as any,
          {
            toolCallId: '',
            messages: [],
            abortSignal: options?.abortSignal,
          } as any,
        );
      },
    });
  }

  /**
   * Returns the flat list of declarative schedules sourced from currently
   * registered workflows. Single-schedule workflows yield one entry keyed by
   * `wf_<encoded(workflowId)>`. Array-form workflows yield one entry per array
   * entry keyed by `wf_<encoded(workflowId)>__<encoded(scheduleId)>` so the
   * prefix uniquely identifies "all rows owned by this workflow's declarative
   * config" even when ids contain `__` or other delimiter-like characters.
   */
  #collectDeclarativeSchedules(): Array<{
    scheduleId: string;
    workflowId: string;
    cfg: WorkflowScheduleConfig;
  }> {
    const out: Array<{ scheduleId: string; workflowId: string; cfg: WorkflowScheduleConfig }> = [];
    const workflows = this.#workflows as Record<string, AnyWorkflow>;
    for (const workflow of Object.values(workflows ?? {})) {
      const configs = collectWorkflowScheduleConfigs(workflow);
      if (configs.length === 0) continue;
      const isArrayForm = configs.length > 1 || (configs.length === 1 && configs[0]!.id !== undefined);
      for (const cfg of configs) {
        const scheduleId = isArrayForm
          ? declarativeScheduleRowId(workflow.id, cfg.id)
          : declarativeScheduleRowId(workflow.id);
        out.push({ scheduleId, workflowId: workflow.id, cfg });
      }
    }
    return out;
  }

  #shouldEnableScheduler(): boolean {
    if (this.#schedulerConfig?.enabled === false) return false;
    if (this.#schedulerConfig?.enabled === true) return true;
    return this.#hasScheduledWorkflow;
  }

  /**
   * Find the SchedulerWorker from the workers list (if present).
   */
  #findSchedulerWorker(): SchedulerWorker | undefined {
    return this.#workers.find((w): w is SchedulerWorker => w.name === 'scheduler') as SchedulerWorker | undefined;
  }

  /**
   * Sync code-declared schedule configs to the database. Called by
   * SchedulerWorker during init and by addWorkflow() for late registrations.
   *
   * @internal — public so SchedulerWorker can call it, not part of the user API.
   */
  async registerDeclarativeSchedules(schedulesStore: SchedulesStorage): Promise<void> {
    const declared = this.#collectDeclarativeSchedules();
    const declaredIds = new Set(declared.map(d => d.scheduleId));

    // Group declared ids by workflow so we can detect orphans (rows that
    // start with `wf_<encoded(workflowId)>` but aren't in the current declared
    // set). Seed an empty entry for every registered workflow first so that
    // workflows which removed all their schedules across a redeploy still
    // have their old rows cleaned up.
    const declaredIdsByWorkflow = new Map<string, Set<string>>();
    const workflows = this.#workflows as Record<string, AnyWorkflow> | undefined;
    for (const workflow of Object.values(workflows ?? {})) {
      declaredIdsByWorkflow.set(workflow.id, new Set());
    }
    for (const { workflowId, scheduleId } of declared) {
      if (!declaredIdsByWorkflow.has(workflowId)) declaredIdsByWorkflow.set(workflowId, new Set());
      declaredIdsByWorkflow.get(workflowId)!.add(scheduleId);
    }

    for (const { scheduleId, workflowId, cfg } of declared) {
      try {
        const existing = await schedulesStore.getSchedule(scheduleId);
        const now = Date.now();
        const target: Schedule['target'] = {
          type: 'workflow',
          workflowId,
          inputData: cfg.inputData,
          initialState: cfg.initialState,
          requestContext: cfg.requestContext,
        };

        if (!existing) {
          await schedulesStore.createSchedule({
            id: scheduleId,
            target,
            cron: cfg.cron,
            timezone: cfg.timezone,
            status: 'active',
            nextFireAt: computeNextFireAt(cfg.cron, { timezone: cfg.timezone, after: now }),
            createdAt: now,
            updatedAt: now,
            metadata: cfg.metadata,
          });
          continue;
        }

        // Diff config fields and patch the existing row if anything changed.
        // We deliberately leave `status` alone — a row may have been paused
        // out-of-band via storage, and a redeploy shouldn't unpause it.
        const patch: ScheduleUpdate = {};
        const cronChanged = existing.cron !== cfg.cron;
        const timezoneChanged = (existing.timezone ?? undefined) !== (cfg.timezone ?? undefined);

        if (cronChanged) patch.cron = cfg.cron;
        if (timezoneChanged) patch.timezone = cfg.timezone;
        if (!targetsEqual(existing.target, target)) patch.target = target;
        if (!metadataEqual(existing.metadata, cfg.metadata)) patch.metadata = cfg.metadata;

        // Cron or timezone change invalidates the stored nextFireAt — recompute
        // from now so we don't fire on the old schedule.
        if (cronChanged || timezoneChanged) {
          patch.nextFireAt = computeNextFireAt(cfg.cron, { timezone: cfg.timezone, after: now });
        }

        if (Object.keys(patch).length > 0) {
          await schedulesStore.updateSchedule(scheduleId, patch);
        }
      } catch (error) {
        this.#logger?.error('Failed to register declarative schedule', { scheduleId, workflowId, error });
      }
    }

    // Orphan deletion: drop any Mastra-managed declarative schedule rows
    // (id starts with `wf_<workflowId>` or `wf_<workflowId>__`) that are no
    // longer declared in code. This covers two cases:
    //   1. A registered workflow's array-form entries shrunk across deploys.
    //   2. The owning workflow itself was deleted from code. Leaving these
    //      rows behind would have the scheduler keep firing for a workflow
    //      the processor can't resolve, producing infinite event-redelivery
    //      loops (see WorkflowEventProcessor#dispatch).
    // User-created schedules (via the schedules API) don't use the `wf_`
    // prefix, so they're untouched.
    const allRows = await schedulesStore.listSchedules();
    for (const row of allRows) {
      if (declaredIds.has(row.id)) continue;
      if (!row.id.startsWith('wf_')) continue;
      const ownerWorkflowId = ownerWorkflowIdForRow(row.id, declaredIdsByWorkflow) ?? ownerWorkflowIdFromRowId(row.id);
      if (!ownerWorkflowId) continue;
      try {
        await schedulesStore.deleteSchedule(row.id);
      } catch (error) {
        this.#logger?.error('Failed to delete orphaned declarative schedule', {
          scheduleId: row.id,
          workflowId: ownerWorkflowId,
          error,
        });
      }
    }
  }

  /**
   * Auto-enables the background task manager when an agent with sub-agents is
   * registered. Sub-agent delegation runs in the background by default so the
   * parent stream stays responsive; that requires the manager to be available.
   * No-op when the user explicitly opted out via `backgroundTasks.enabled: false`.
   *
   * Eligible agents: any agent whose `agents` field is either a static record
   * with at least one entry OR a dynamic (function-based) resolver. Function
   * resolvers are evaluated per request, so we can't inspect their contents
   * here — but if the caller bothered to wire one up, we enable defensively
   * so those resolved sub-agents also dispatch in the background.
   */
  #maybeEnableBackgroundTasksForAgent(agent: Agent<any>): void {
    // Already running — nothing to do
    if (this.#backgroundTaskManager) return;

    // Explicit opt-out
    if (this.#backgroundTaskConfig?.enabled === false) return;

    if (!agent.__hasSubAgentsConfigured?.()) return;

    this.#backgroundTaskConfig = { ...(this.#backgroundTaskConfig ?? {}), enabled: true };
    this.#ensureBackgroundTaskManager();
  }

  /**
   * Retrieves a registered agent by its name.
   *
   * @template TAgentName - The specific agent name type from the registered agents
   * @throws {MastraError} When the agent with the specified name is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   agents: {
   *     weatherAgent: new Agent({
   *       id: 'weather-agent',
   *       name: 'weather-agent',
   *       instructions: 'You provide weather information',
   *       model: 'openai/gpt-5'
   *     })
   *   }
   * });
   * const agent = mastra.getAgent('weatherAgent');
   * const response = await agent.generate('What is the weather?');
   * ```
   */
  public getAgent<TAgentName extends keyof TAgents>(name: TAgentName): TAgents[TAgentName];
  public getAgent<TAgentName extends keyof TAgents>(
    name: TAgentName,
    version: { versionId: string } | { status?: 'draft' | 'published' },
  ): Promise<TAgents[TAgentName]>;
  public getAgent<TAgentName extends keyof TAgents>(
    name: TAgentName,
    version?: { versionId: string } | { status?: 'draft' | 'published' },
  ): TAgents[TAgentName] | Promise<TAgents[TAgentName]> {
    const agent = this.#agents?.[name];
    if (!agent) {
      const error = new MastraError({
        id: 'MASTRA_GET_AGENT_BY_NAME_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Agent with name ${String(name)} not found`,
        details: {
          status: 404,
          agentName: String(name),
          agents: Object.keys(this.#agents ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    if (!version) {
      return this.#agents[name];
    }

    return this.resolveVersionedAgent(agent, version);
  }

  /**
   * Returns the `AgentChannels` instances for all registered agents.
   * Keys are agent IDs.
   */
  public getChannels(): Record<string, AgentChannels> {
    const result: Record<string, AgentChannels> = {};
    for (const [agentKey, agent] of Object.entries(this.#agents ?? {})) {
      const agentChannels = agent.getChannels();
      if (agentChannels instanceof AgentChannels) {
        result[agentKey] = agentChannels;
      }
    }
    return result;
  }

  /**
   * Retrieves a registered agent by its unique ID.
   *
   * This method searches for an agent using its internal ID property. If no agent
   * is found with the given ID, it also attempts to find an agent using the ID as
   * a name.
   *
   * @throws {MastraError} When no agent is found with the specified ID
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   agents: {
   *     assistant: new Agent({
   *       id: 'assistant',
   *       name: 'assistant',
   *       instructions: 'You are a helpful assistant',
   *       model: 'openai/gpt-5'
   *     })
   *   }
   * });
   *
   * const assistant = mastra.getAgent('assistant');
   * const sameAgent = mastra.getAgentById(assistant.id);
   * ```
   */
  public getAgentById<TAgentName extends keyof TAgents>(id: TAgents[TAgentName]['id']): TAgents[TAgentName];
  public getAgentById<TAgentName extends keyof TAgents>(
    id: TAgents[TAgentName]['id'],
    version: { versionId: string } | { status?: 'draft' | 'published' },
  ): Promise<TAgents[TAgentName]>;
  public getAgentById<TAgentName extends keyof TAgents>(
    id: TAgents[TAgentName]['id'],
    version?: { versionId: string } | { status?: 'draft' | 'published' },
  ): TAgents[TAgentName] | Promise<TAgents[TAgentName]> {
    let agent = Object.values(this.#agents).find(a => a.id === id);

    if (!agent) {
      try {
        agent = this.getAgent(id as keyof TAgents) as TAgents[TAgentName];
      } catch {
        // do nothing
      }
    }

    if (!agent) {
      const error = new MastraError({
        id: 'MASTRA_GET_AGENT_BY_AGENT_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Agent with id ${String(id)} not found`,
        details: {
          status: 404,
          agentId: String(id),
          agents: Object.keys(this.#agents ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    if (!version) {
      return agent as TAgents[TAgentName];
    }

    return this.resolveVersionedAgent(agent as TAgents[TAgentName], version);
  }

  /**
   * Resolve a versioned variant of an agent by applying stored overrides from the editor.
   *
   * Requires the editor package to be configured — throws
   * `MASTRA_EDITOR_REQUIRED_FOR_VERSIONED_AGENT_LOOKUP` if it is not.
   *
   * @param agent - The code-defined agent to resolve a version for.
   * @param version - Selects a version by ID or publication status.
   * @returns A forked agent instance with the stored overrides applied.
   */
  public async resolveVersionedAgent<TAgent extends Agent>(
    agent: TAgent,
    version: VersionSelector | { status?: 'draft' | 'published' },
  ): Promise<TAgent> {
    const editor = this.getEditor();

    if (!editor) {
      const error = new MastraError({
        id: 'MASTRA_EDITOR_REQUIRED_FOR_VERSIONED_AGENT_LOOKUP',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: 'Versioned agent lookup requires the editor package to be configured',
        details: {
          status: 400,
          agentId: agent.id,
          ...(version && 'versionId' in version ? { versionId: version.versionId } : {}),
          ...(version && 'status' in version && version.status ? { versionStatus: version.status } : {}),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    return editor.agent.applyStoredOverrides(
      agent,
      'versionId' in version ? version : { status: version.status ?? 'published' },
    ) as Promise<TAgent>;
  }

  /**
   * Returns all registered agents as a record keyed by their names.
   *
   * This method provides access to the complete registry of agents, allowing you to
   * iterate over them, check what agents are available, or perform bulk operations.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   agents: {
   *     weatherAgent: new Agent({ id: 'weather-agent', name: 'weather', model: 'openai/gpt-4o' }),
   *     supportAgent: new Agent({ id: 'support-agent', name: 'support', model: 'openai/gpt-4o' })
   *   }
   * });
   *
   * const allAgents = mastra.listAgents();
   * console.log(Object.keys(allAgents)); // ['weatherAgent', 'supportAgent']
   * ```
   */
  public listAgents() {
    return this.#agents;
  }

  /**
   * Adds a new agent to the Mastra instance.
   *
   * This method allows dynamic registration of agents after the Mastra instance
   * has been created. The agent will be initialized with the current logger.
   *
   * @throws {MastraError} When an agent with the same key already exists
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newAgent = new Agent({
   *   id: 'chat-agent',
   *   name: 'Chat Assistant',
   *   model: 'openai/gpt-4o'
   * });
   * mastra.addAgent(newAgent); // Uses agent.id as key
   * // or
   * mastra.addAgent(newAgent, 'customKey'); // Uses custom key
   *
   * // Durable agents (e.g., InngestAgent) are also supported:
   * const durableAgent = createInngestAgent({ agent: newAgent, inngest });
   * mastra.addAgent(durableAgent); // Auto-registers required workflows
   * ```
   */
  public addAgent<A extends Agent | ToolLoopAgentLike | DurableAgentLike>(
    agent: A,
    key?: string,
    options?: { source?: DefinitionSource },
  ): void {
    if (!agent) {
      throw createUndefinedPrimitiveError('agent', agent, key);
    }

    // Handle durable agent wrappers (e.g., InngestAgent)
    // These wrap a regular Agent with execution engine-specific capabilities
    if (isDurableAgentLike(agent)) {
      const durableAgent = agent as DurableAgentLike;
      const underlyingAgent = durableAgent.agent;
      const agentKey = key || durableAgent.id;

      // Check if already registered
      const agents = this.#agents as Record<string, Agent<any>>;
      if (agents[agentKey]) {
        const logger = this.getLogger();
        logger.debug(`Agent with key ${agentKey} already exists. Skipping addition.`);
        return;
      }

      // Set the Mastra instance on the durable agent for observability
      durableAgent.__setMastra?.(this);

      // Initialize the underlying agent (needed for tools, memory, etc.)
      underlyingAgent.__setLogger(this.#logger);
      underlyingAgent.__registerMastra(this);
      underlyingAgent.__registerPrimitives({
        logger: this.getLogger(),
        storage: this.getStorage(),
        agents: agents,
        tts: this.#tts,
        vectors: this.#vectors,
      });

      // Store the durable wrapper in #agents (not the underlying agent)
      // This ensures getAgentById returns the wrapper so .stream() uses durable execution.
      // The cast is safe because DurableAgent extends Agent directly, and InngestAgent uses
      // a Proxy that forwards all Agent method calls to the underlying agent.
      agents[agentKey] = durableAgent as unknown as Agent<any>;

      // Register durable workflows if the wrapper provides them
      const durableWorkflows = durableAgent.getDurableWorkflows?.() ?? [];
      for (const workflow of durableWorkflows) {
        this.addWorkflow(workflow, workflow.id);
      }

      return;
    }

    let mastraAgent: Agent<any, any, any>;
    if (isToolLoopAgentLike(agent)) {
      // Pass the config key as the name if the ToolLoopAgent doesn't have an id
      mastraAgent = toolLoopAgentToMastraAgent(agent, { fallbackName: key });
    } else {
      mastraAgent = agent as Agent;
    }
    const agentKey = key || mastraAgent.id;
    const agents = this.#agents as Record<string, Agent<any>>;
    if (agents[agentKey]) {
      return;
    }

    // Initialize the agent
    mastraAgent.__setLogger(this.#logger);
    mastraAgent.__registerMastra(this);
    mastraAgent.__registerPrimitives({
      logger: this.getLogger(),
      storage: this.getStorage(),
      agents: agents,
      tts: this.#tts,
      vectors: this.#vectors,
    });

    // Set the source if provided
    if (options?.source) {
      mastraAgent.source = options.source;
    }

    agents[agentKey] = mastraAgent;

    // Register configured processor workflows from the agent
    // Use .then() to handle async resolution without blocking the constructor
    // This excludes memory-derived processors to avoid triggering memory factory functions
    mastraAgent
      .getConfiguredProcessorWorkflows()
      .then(processorWorkflows => {
        for (const workflow of processorWorkflows) {
          this.addWorkflow(workflow, workflow.id);
        }
      })
      .catch(err => {
        this.#logger?.debug(`Failed to register processor workflows for agent ${agentKey}:`, err);
      });

    // Register agent workspace in the workspaces registry for direct lookup.
    // Dynamic workspace functions may return undefined without request context — that's fine,
    // the if (workspace) guard below will skip registration and they'll register lazily later.
    if (mastraAgent.hasOwnWorkspace?.()) {
      Promise.resolve(mastraAgent.getWorkspace?.())
        .then(workspace => {
          if (workspace) {
            this.addWorkspace(workspace, undefined, {
              source: 'agent',
              agentId: mastraAgent.id ?? agentKey,
              agentName: mastraAgent.name,
            });
          }
        })
        .catch(err => {
          this.#logger?.debug(`Failed to register workspace for agent ${agentKey}:`, err);
        });
    }

    // Register scorers from the agent to the Mastra instance
    // This makes agent-level scorers discoverable via mastra.getScorer()/getScorerById()
    mastraAgent
      .listScorers()
      .then(scorers => {
        for (const [, entry] of Object.entries(scorers || {})) {
          this.addScorer(entry.scorer, undefined, { source: 'code' });
        }
      })
      .catch(err => {
        this.#logger?.debug(`Failed to register scorers from agent ${agentKey}:`, err);
      });

    // Set up AgentChannels for manual adapter configurations
    const agentChannelsInstance = mastraAgent.getChannels();
    if (agentChannelsInstance) {
      agentChannelsInstance.__setLogger(this.#logger);
      const channelRoutes = agentChannelsInstance.getWebhookRoutes();
      if (channelRoutes.length > 0) {
        this.#server = {
          ...this.#server,
          apiRoutes: [...(this.#server?.apiRoutes ?? []), ...channelRoutes],
        };
      }
      void agentChannelsInstance.initialize(this);
    }
  }

  /**
   * Removes an agent from the Mastra instance by its key or ID.
   * Used when stored agents are updated/deleted to allow fresh data to be loaded.
   *
   * @param keyOrId - The agent key or ID to remove
   * @returns true if an agent was removed, false if no agent was found
   *
   * @example
   * ```typescript
   * // Remove by key
   * mastra.removeAgent('myAgent');
   *
   * // Remove by ID
   * mastra.removeAgent('agent-123');
   * ```
   */
  public removeAgent(keyOrId: string): boolean {
    const agents = this.#agents as Record<string, Agent<any>>;

    // Try direct key lookup first
    if (agents[keyOrId]) {
      const agentId = agents[keyOrId]?.id;
      delete agents[keyOrId];
      // Clear from stored agents cache to prevent stale data
      if (agentId) {
        this.#storedAgentsCache.delete(agentId);
      }
      return true;
    }

    // Try finding by ID
    const key = Object.keys(agents).find(k => agents[k]?.id === keyOrId);
    if (key) {
      const agentId = agents[key]?.id;
      delete agents[key];
      // Clear from stored agents cache to prevent stale data
      if (agentId) {
        this.#storedAgentsCache.delete(agentId);
      }
      return true;
    }

    return false;
  }

  /**
   * Retrieves a registered vector store by its name.
   *
   * @template TVectorName - The specific vector store name type from the registered vectors
   * @throws {MastraError} When the vector store with the specified name is not found
   *
   * @example Using a vector store for semantic search
   * ```typescript
   * import { PineconeVector } from '@mastra/pinecone';
   * import { OpenAIEmbedder } from '@mastra/embedders';
   *
   * const mastra = new Mastra({
   *   vectors: {
   *     knowledge: new PineconeVector({
   *       apiKey: process.env.PINECONE_API_KEY,
   *       indexName: 'knowledge-base',
   *       embedder: new OpenAIEmbedder({
   *         apiKey: process.env.OPENAI_API_KEY,
   *         model: 'text-embedding-3-small'
   *       })
   *     }),
   *     products: new PineconeVector({
   *       apiKey: process.env.PINECONE_API_KEY,
   *       indexName: 'product-catalog'
   *     })
   *   }
   * });
   *
   * // Get a vector store and perform semantic search
   * const knowledgeBase = mastra.getVector('knowledge');
   * const results = await knowledgeBase.query({
   *   query: 'How to reset password?',
   *   topK: 5
   * });
   *
   * console.log('Relevant documents:', results);
   * ```
   */
  public getVector<TVectorName extends keyof TVectors>(name: TVectorName): TVectors[TVectorName] {
    const vector = this.#vectors?.[name];
    if (!vector) {
      const error = new MastraError({
        id: 'MASTRA_GET_VECTOR_BY_NAME_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Vector with name ${String(name)} not found`,
        details: {
          status: 404,
          vectorName: String(name),
          vectors: Object.keys(this.#vectors ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return vector;
  }

  /**
   * Retrieves a specific vector store instance by its ID.
   *
   * This method searches for a vector store by its internal ID property.
   * If not found by ID, it falls back to searching by registration key.
   *
   * @throws {MastraError} When the specified vector store is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   vectors: {
   *     embeddings: chromaVector
   *   }
   * });
   *
   * const vectorStore = mastra.getVectorById('chroma-123');
   * ```
   */
  public getVectorById<TVectorName extends keyof TVectors>(id: TVectors[TVectorName]['id']): TVectors[TVectorName] {
    const allVectors = this.#vectors ?? ({} as Record<string, MastraVector>);

    // First try to find by internal ID
    for (const vector of Object.values(allVectors)) {
      if (vector.id === id) {
        return vector as TVectors[TVectorName];
      }
    }

    // Fallback to searching by registration key
    const vectorByKey = allVectors[id];
    if (vectorByKey) {
      return vectorByKey as TVectors[TVectorName];
    }

    const error = new MastraError({
      id: 'MASTRA_GET_VECTOR_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Vector store with id ${id} not found`,
      details: {
        status: 404,
        vectorId: String(id),
        vectors: Object.keys(allVectors).join(', '),
      },
    });
    this.#logger?.trackException(error);
    throw error;
  }

  /**
   * Returns all registered vector stores as a record keyed by their names.
   *
   * @example Listing all vector stores
   * ```typescript
   * const mastra = new Mastra({
   *   vectors: {
   *     documents: new PineconeVector({ indexName: 'docs' }),
   *     images: new PineconeVector({ indexName: 'images' }),
   *     products: new ChromaVector({ collectionName: 'products' })
   *   }
   * });
   *
   * const allVectors = mastra.getVectors();
   * console.log(Object.keys(allVectors)); // ['documents', 'images', 'products']
   *
   * // Check vector store types and configurations
   * for (const [name, vectorStore] of Object.entries(allVectors)) {
   *   console.log(`Vector store ${name}:`, vectorStore.constructor.name);
   * }
   * ```
   */
  public listVectors(): TVectors | undefined {
    return this.#vectors;
  }

  /**
   * Adds a new vector store to the Mastra instance.
   *
   * This method allows dynamic registration of vector stores after the Mastra instance
   * has been created. The vector store will be initialized with the current logger.
   *
   * @throws {MastraError} When a vector store with the same key already exists
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newVector = new ChromaVector({ id: 'chroma-embeddings' });
   * mastra.addVector(newVector); // Uses vector.id as key
   * // or
   * mastra.addVector(newVector, 'customKey'); // Uses custom key
   * ```
   */
  public addVector<V extends MastraVector>(vector: V, key?: string): void {
    if (!vector) {
      throw createUndefinedPrimitiveError('vector', vector, key);
    }
    const vectorKey = key || vector.id;
    const vectors = this.#vectors as Record<string, MastraVector>;
    if (vectors[vectorKey]) {
      return;
    }

    // Initialize the vector with the logger
    vector.__setLogger(this.#logger || this.getLogger());
    vectors[vectorKey] = vector;
  }

  /**
   * @deprecated Use listVectors() instead
   */
  public getVectors(): TVectors | undefined {
    console.warn('getVectors() is deprecated. Use listVectors() instead.');
    return this.listVectors();
  }

  /**
   * Gets the currently configured deployment provider.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   deployer: new VercelDeployer({
   *     token: process.env.VERCEL_TOKEN,
   *     projectId: process.env.VERCEL_PROJECT_ID
   *   })
   * });
   *
   * const deployer = mastra.getDeployer();
   * if (deployer) {
   *   await deployer.deploy({
   *     name: 'my-mastra-app',
   *     environment: 'production'
   *   });
   * }
   * ```
   */
  public getDeployer() {
    return this.#deployer;
  }

  /**
   * Gets the global workspace instance.
   * Workspace provides file storage, skills, and code execution capabilities.
   * Agents inherit this workspace unless they have their own configured.
   *
   * @example
   * ```typescript
   * const workspace = mastra.getWorkspace();
   * if (workspace?.skills) {
   *   const skills = await workspace.skills.list();
   * }
   * ```
   */
  public getWorkspace(): Workspace | undefined {
    return this.#workspace;
  }

  /**
   * Retrieves a registered workspace by its ID.
   *
   * @throws {MastraError} When the workspace with the specified ID is not found
   *
   * @example
   * ```typescript
   * const workspace = mastra.getWorkspaceById('workspace-123');
   * const files = await workspace.filesystem.readdir('/');
   * ```
   */
  public getWorkspaceById(id: string): Workspace {
    const entry = this.#workspaces[id];
    if (!entry) {
      const error = new MastraError({
        id: 'MASTRA_GET_WORKSPACE_BY_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Workspace with id ${id} not found`,
        details: {
          status: 404,
          workspaceId: id,
          availableIds: Object.keys(this.#workspaces).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return entry.workspace;
  }

  /**
   * Returns all registered workspaces as a record keyed by their IDs.
   *
   * @example
   * ```typescript
   * const workspaces = mastra.listWorkspaces();
   * for (const [id, entry] of Object.entries(workspaces)) {
   *   console.log(`Workspace ${id}: ${entry.workspace.name} (source: ${entry.source})`);
   * }
   * ```
   */
  public listWorkspaces(): Record<string, RegisteredWorkspace> {
    return { ...this.#workspaces };
  }

  /**
   * Adds a new workspace to the Mastra instance.
   *
   * This method allows dynamic registration of workspaces after the Mastra instance
   * has been created. Workspaces are keyed by their ID.
   *
   * @example
   * ```typescript
   * const workspace = new Workspace({
   *   id: 'project-workspace',
   *   name: 'Project Workspace',
   *   filesystem: new LocalFilesystem({ rootPath: './workspace' })
   * });
   * mastra.addWorkspace(workspace);
   * ```
   */
  public addWorkspace(
    workspace: AnyWorkspace,
    key?: string,
    metadata?: { source?: 'mastra' | 'agent'; agentId?: string; agentName?: string },
  ): void {
    if (!workspace) {
      throw createUndefinedPrimitiveError('workspace', workspace, key);
    }
    const source = metadata?.source ?? (metadata?.agentId || metadata?.agentName ? 'agent' : 'mastra');
    if (source === 'agent' && (!metadata?.agentId || !metadata?.agentName)) {
      throw new MastraError({
        id: 'MASTRA_ADD_WORKSPACE_MISSING_AGENT_METADATA',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: 'Agent workspaces must include agentId and agentName.',
        details: { status: 400, workspaceId: key || workspace.id },
      });
    }
    const workspaceKey = key || workspace.id;
    if (this.#workspaces[workspaceKey]) {
      return;
    }

    this.#workspaces[workspaceKey] = {
      workspace,
      source,
      ...(metadata?.agentId ? { agentId: metadata.agentId } : {}),
      ...(metadata?.agentName ? { agentName: metadata.agentName } : {}),
    };
  }

  /**
   * Retrieves a registered workflow by its ID.
   *
   * @template TWorkflowId - The specific workflow ID type from the registered workflows
   * @throws {MastraError} When the workflow with the specified ID is not found
   *
   * @example Getting and executing a workflow
   * ```typescript
   * import { createWorkflow, createStep } from '@mastra/core/workflows';
   * import { z } from 'zod/v4';
   *
   * const processDataWorkflow = createWorkflow({
   *   name: 'process-data',
   *   triggerSchema: z.object({ input: z.string() })
   * })
   *   .then(validateStep)
   *   .then(transformStep)
   *   .then(saveStep)
   *   .commit();
   *
   * const mastra = new Mastra({
   *   workflows: {
   *     dataProcessor: processDataWorkflow
   *   }
   * });
   * ```
   */
  public getWorkflow<TWorkflowId extends keyof TWorkflows>(
    id: TWorkflowId,
    { serialized }: { serialized?: boolean } = {},
  ): TWorkflows[TWorkflowId] {
    const workflow = this.#workflows?.[id];
    if (!workflow) {
      const error = new MastraError({
        id: 'MASTRA_GET_WORKFLOW_BY_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Workflow with ID ${String(id)} not found`,
        details: {
          status: 404,
          workflowId: String(id),
          workflows: Object.keys(this.#workflows ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    if (serialized) {
      return { name: workflow.name } as TWorkflows[TWorkflowId];
    }

    return workflow;
  }

  __registerInternalWorkflow(workflow: Workflow) {
    workflow.__registerMastra(this);
    workflow.__registerPrimitives({
      logger: this.getLogger(),
    });
    this.#internalMastraWorkflows[workflow.id] = workflow;
  }

  __hasInternalWorkflow(id: string): boolean {
    return Object.values(this.#internalMastraWorkflows).some(workflow => workflow.id === id);
  }

  __getInternalWorkflow(id: string): Workflow {
    const workflow = Object.values(this.#internalMastraWorkflows).find(a => a.id === id);
    if (!workflow) {
      throw new MastraError({
        id: 'MASTRA_GET_INTERNAL_WORKFLOW_BY_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.SYSTEM,
        text: `Workflow with id ${String(id)} not found`,
        details: {
          status: 404,
          workflowId: String(id),
        },
      });
    }

    return workflow;
  }

  /**
   * Retrieves a registered workflow by its unique ID.
   *
   * This method searches for a workflow using its internal ID property. If no workflow
   * is found with the given ID, it also attempts to find a workflow using the ID as
   * a name.
   *
   * @throws {MastraError} When no workflow is found with the specified ID
   *
   * @example Finding a workflow by ID
   * ```typescript
   * const mastra = new Mastra({
   *   workflows: {
   *     dataProcessor: createWorkflow({
   *       name: 'process-data',
   *       triggerSchema: z.object({ input: z.string() })
   *     }).commit()
   *   }
   * });
   *
   * // Get the workflow's ID
   * const workflow = mastra.getWorkflow('dataProcessor');
   * const workflowId = workflow.id;
   *
   * // Later, retrieve the workflow by ID
   * const sameWorkflow = mastra.getWorkflowById(workflowId);
   * console.log(sameWorkflow.name); // "process-data"
   * ```
   */
  public getWorkflowById<TWorkflowName extends keyof TWorkflows>(
    id: TWorkflows[TWorkflowName]['id'],
  ): TWorkflows[TWorkflowName] {
    let workflow = Object.values(this.#workflows).find(a => a.id === id);

    if (!workflow) {
      try {
        workflow = this.getWorkflow(id);
      } catch {
        // do nothing
      }
    }

    if (!workflow) {
      const error = new MastraError({
        id: 'MASTRA_GET_WORKFLOW_BY_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Workflow with id ${String(id)} not found`,
        details: {
          status: 404,
          workflowId: String(id),
          workflows: Object.keys(this.#workflows ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    return workflow as TWorkflows[TWorkflowName];
  }

  public async listActiveWorkflowRuns(): Promise<WorkflowRuns> {
    const storage = this.#storage;
    if (!storage) {
      this.#logger.debug('Cannot get active workflow runs. Mastra storage is not initialized');
      return { runs: [], total: 0 };
    }

    // Get all workflows with default engine type
    const defaultEngineWorkflows = Object.values(this.#workflows).filter(workflow => workflow.engineType === 'default');

    const activeRunsByWorkflow = await Promise.all(
      defaultEngineWorkflows.map(workflow => workflow.listActiveWorkflowRuns()),
    );

    const allRuns = activeRunsByWorkflow.flatMap(activeRuns => activeRuns.runs);
    const allTotal = activeRunsByWorkflow.reduce((total, activeRuns) => total + activeRuns.total, 0);

    return {
      runs: allRuns,
      total: allTotal,
    };
  }

  public async restartAllActiveWorkflowRuns(): Promise<void> {
    const activeRuns = await this.listActiveWorkflowRuns();
    if (activeRuns.runs.length > 0) {
      this.#logger.debug(
        `Restarting ${activeRuns.runs.length} active workflow run${activeRuns.runs.length > 1 ? 's' : ''}`,
      );
    }
    for (const runSnapshot of activeRuns.runs) {
      const workflow = this.getWorkflowById(runSnapshot.workflowName);
      try {
        const run = await workflow.createRun({ runId: runSnapshot.runId });
        await run.restart();
        this.#logger.debug('Restarted workflow run', { workflow: runSnapshot.workflowName, runId: runSnapshot.runId });
      } catch (error) {
        this.#logger.error('Failed to restart workflow run', {
          workflow: runSnapshot.workflowName,
          runId: runSnapshot.runId,
          error,
        });
      }
    }
  }

  /**
   * Returns all registered scorers as a record keyed by their IDs.
   *
   * @example Listing all scorers
   * ```typescript
   * import { HelpfulnessScorer, AccuracyScorer, RelevanceScorer } from '@mastra/scorers';
   *
   * const mastra = new Mastra({
   *   scorers: {
   *     helpfulness: new HelpfulnessScorer(),
   *     accuracy: new AccuracyScorer(),
   *     relevance: new RelevanceScorer()
   *   }
   * });
   *
   * const allScorers = mastra.listScorers();
   * console.log(Object.keys(allScorers)); // ['helpfulness', 'accuracy', 'relevance']
   *
   * // Check scorer configurations
   * for (const [id, scorer] of Object.entries(allScorers)) {
   *   console.log(`Scorer ${id}:`, scorer.id, scorer.name, scorer.description);
   * }
   * ```
   */
  public listScorers() {
    return this.#scorers;
  }

  /**
   * Adds a new scorer to the Mastra instance.
   *
   * This method allows dynamic registration of scorers after the Mastra instance
   * has been created.
   *
   * If a scorer with the same key already exists, this method leaves the existing
   * scorer registered and returns.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newScorer = new MastraScorer({
   *   id: 'quality-scorer',
   *   name: 'Quality Scorer'
   * });
   * mastra.addScorer(newScorer); // Uses scorer.id as key
   * // or
   * mastra.addScorer(newScorer, 'customKey'); // Uses custom key
   * ```
   */
  public addScorer<S extends MastraScorer<any, any, any, any>>(
    scorer: S,
    key?: string,
    options?: { source?: DefinitionSource },
  ): void {
    if (!scorer) {
      throw createUndefinedPrimitiveError('scorer', scorer, key);
    }
    const scorerKey = key || scorer.id;
    const scorers = this.#scorers as Record<string, MastraScorer<any, any, any, any>>;
    if (scorers[scorerKey]) {
      return;
    }

    // Register Mastra instance with scorer to enable custom gateway access
    scorer.__registerMastra(this);

    // Set the source if provided
    if (options?.source) {
      scorer.source = options.source;
    }

    scorers[scorerKey] = scorer;
  }

  /**
   * Retrieves a registered scorer by its key.
   *
   * @template TScorerKey - The specific scorer key type from the registered scorers
   * @throws {MastraError} When the scorer with the specified key is not found
   *
   * @example Getting and using a scorer
   * ```typescript
   * import { HelpfulnessScorer, AccuracyScorer } from '@mastra/scorers';
   *
   * const mastra = new Mastra({
   *   scorers: {
   *     helpfulness: new HelpfulnessScorer({
   *       model: 'openai/gpt-4o',
   *       criteria: 'Rate how helpful this response is'
   *     }),
   *     accuracy: new AccuracyScorer({
   *       model: 'openai/gpt-5'
   *     })
   *   }
   * });
   *
   * // Get a specific scorer
   * const helpfulnessScorer = mastra.getScorer('helpfulness');
   * const score = await helpfulnessScorer.score({
   *   input: 'How do I reset my password?',
   *   output: 'You can reset your password by clicking the forgot password link.',
   *   expected: 'Detailed password reset instructions'
   * });
   *
   * console.log('Helpfulness score:', score);
   * ```
   */
  public getScorer<TScorerKey extends keyof TScorers>(key: TScorerKey): TScorers[TScorerKey] {
    const scorer = this.#scorers?.[key];
    if (!scorer) {
      const error = new MastraError({
        id: 'MASTRA_GET_SCORER_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Scorer with ${String(key)} not found`,
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return scorer;
  }

  /**
   * Retrieves a registered scorer by its name.
   *
   * This method searches through all registered scorers to find one with the specified name.
   * Unlike `getScorer()` which uses the registration key, this method uses the scorer's
   * internal name property.
   *
   * @throws {MastraError} When no scorer is found with the specified name
   *
   * @example Finding a scorer by name
   * ```typescript
   * import { HelpfulnessScorer } from '@mastra/scorers';
   *
   * const mastra = new Mastra({
   *   scorers: {
   *     myHelpfulnessScorer: new HelpfulnessScorer({
   *       name: 'helpfulness-evaluator',
   *       model: 'openai/gpt-5'
   *     })
   *   }
   * });
   *
   * // Find scorer by its internal name, not the registration key
   * const scorer = mastra.getScorerById('helpfulness-evaluator');
   * const score = await scorer.score({
   *   input: 'question',
   *   output: 'answer'
   * });
   * ```
   */
  public getScorerById<TScorerName extends keyof TScorers>(id: TScorers[TScorerName]['id']): TScorers[TScorerName] {
    for (const [_key, value] of Object.entries(this.#scorers ?? {})) {
      if (value.id === id || value?.name === id) {
        return value as TScorers[TScorerName];
      }
    }

    const error = new MastraError({
      id: 'MASTRA_GET_SCORER_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Scorer with id ${String(id)} not found`,
    });
    this.#logger?.trackException(error);
    throw error;
  }

  /**
   * Removes a scorer from the Mastra instance by its key or ID.
   *
   * @param keyOrId - The scorer key or ID to remove
   * @returns true if a scorer was removed, false if no scorer was found
   */
  public removeScorer(keyOrId: string): boolean {
    const scorers = this.#scorers as Record<string, MastraScorer<any, any, any, any>> | undefined;
    if (!scorers) return false;

    // Try direct key lookup first
    if (scorers[keyOrId]) {
      const scorerId = scorers[keyOrId]?.id;
      delete scorers[keyOrId];
      // Clear from stored scorers cache to prevent stale data
      if (scorerId) {
        this.#storedScorersCache.delete(scorerId);
      }
      return true;
    }

    // Try finding by ID or name
    const key = Object.keys(scorers).find(k => scorers[k]?.id === keyOrId || scorers[k]?.name === keyOrId);
    if (key) {
      const scorerId = scorers[key]?.id;
      delete scorers[key];
      // Clear from stored scorers cache to prevent stale data
      if (scorerId) {
        this.#storedScorersCache.delete(scorerId);
      }
      return true;
    }

    return false;
  }

  // =========================================================================
  // Prompt Blocks
  // =========================================================================

  /**
   * Returns all registered prompt blocks.
   */
  public listPromptBlocks(): Record<string, StorageResolvedPromptBlockType> {
    return this.#promptBlocks;
  }

  /**
   * Registers a prompt block in the Mastra instance's runtime registry.
   *
   * @param promptBlock - The resolved prompt block to register
   * @param key - Optional registration key (defaults to promptBlock.id)
   */
  public addPromptBlock(promptBlock: StorageResolvedPromptBlockType, key?: string): void {
    const blockKey = key || promptBlock.id;
    if (this.#promptBlocks[blockKey]) {
      return;
    }
    this.#promptBlocks[blockKey] = promptBlock;
  }

  /**
   * Retrieves a registered prompt block by its key.
   *
   * @throws {MastraError} When the prompt block with the specified key is not found
   */
  public getPromptBlock(key: string): StorageResolvedPromptBlockType {
    const block = this.#promptBlocks[key];
    if (!block) {
      throw new MastraError({
        id: 'MASTRA_GET_PROMPT_BLOCK_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Prompt block with key ${key} not found`,
      });
    }
    return block;
  }

  /**
   * Retrieves a registered prompt block by its ID.
   *
   * @throws {MastraError} When no prompt block is found with the specified ID
   */
  public getPromptBlockById(id: string): StorageResolvedPromptBlockType {
    for (const [, block] of Object.entries(this.#promptBlocks)) {
      if (block.id === id) {
        return block;
      }
    }

    throw new MastraError({
      id: 'MASTRA_GET_PROMPT_BLOCK_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Prompt block with id ${id} not found`,
    });
  }

  /**
   * Removes a prompt block from the Mastra instance by its key or ID.
   *
   * @param keyOrId - The prompt block key or ID to remove
   * @returns true if a prompt block was removed, false if not found
   */
  public removePromptBlock(keyOrId: string): boolean {
    if (this.#promptBlocks[keyOrId]) {
      delete this.#promptBlocks[keyOrId];
      return true;
    }

    const key = Object.keys(this.#promptBlocks).find(k => this.#promptBlocks[k]?.id === keyOrId);
    if (key) {
      delete this.#promptBlocks[key];
      return true;
    }

    return false;
  }

  /**
   * Retrieves a specific tool by registration key.
   *
   * @throws {MastraError} When the specified tool is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   tools: {
   *     calculator: calculatorTool,
   *     weather: weatherTool
   *   }
   * });
   *
   * const tool = mastra.getTool('calculator');
   * ```
   */
  public getTool<TToolName extends keyof TTools>(name: TToolName): TTools[TToolName] {
    if (!this.#tools || !this.#tools[name]) {
      const error = new MastraError({
        id: 'MASTRA_GET_TOOL_BY_NAME_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Tool with name ${String(name)} not found`,
        details: {
          status: 404,
          toolName: String(name),
          tools: Object.keys(this.#tools ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return this.#tools[name];
  }

  /**
   * Retrieves a specific tool by its ID.
   *
   * @throws {MastraError} When the specified tool is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   tools: {
   *     calculator: calculatorTool
   *   }
   * });
   *
   * const tool = mastra.getToolById('calculator-tool-id');
   * ```
   */
  public getToolById<TToolName extends keyof TTools>(id: TTools[TToolName]['id']): TTools[TToolName] {
    const allTools = this.#tools;

    if (!allTools) {
      throw new MastraError({
        id: 'MASTRA_GET_TOOL_BY_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Tool with id ${id} not found`,
      });
    }
    // First try to find by internal ID
    for (const tool of Object.values(allTools)) {
      if (tool.id === id) {
        return tool as TTools[TToolName];
      }
    }

    // Fallback to searching by registration key
    const toolByKey = allTools[id];
    if (toolByKey) {
      return toolByKey as TTools[TToolName];
    }

    const error = new MastraError({
      id: 'MASTRA_GET_TOOL_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Tool with id ${id} not found`,
      details: {
        status: 404,
        toolId: String(id),
        tools: Object.keys(allTools).join(', '),
      },
    });
    this.#logger?.trackException(error);
    throw error;
  }

  /**
   * Lists all configured tools.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   tools: {
   *     calculator: calculatorTool,
   *     weather: weatherTool
   *   }
   * });
   *
   * const tools = mastra.listTools();
   * Object.entries(tools || {}).forEach(([name, tool]) => {
   *   console.log(`Tool "${name}":`, tool.id);
   * });
   * ```
   */
  public listTools(): TTools | undefined {
    return this.#tools;
  }

  /**
   * Adds a new tool to the Mastra instance.
   *
   * This method allows dynamic registration of tools after the Mastra instance
   * has been created.
   *
   * @throws {MastraError} When a tool with the same key already exists
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newTool = createTool({
   *   id: 'calculator-tool',
   *   description: 'Performs calculations'
   * });
   * mastra.addTool(newTool); // Uses tool.id as key
   * // or
   * mastra.addTool(newTool, 'customKey'); // Uses custom key
   * ```
   */
  public addTool<T extends ToolAction<any, any, any, any>>(tool: T, key?: string): void {
    if (!tool) {
      throw createUndefinedPrimitiveError('tool', tool, key);
    }
    const toolKey = key || tool.id;
    const tools = this.#tools as Record<string, ToolAction<any, any, any, any>>;
    if (tools[toolKey]) {
      return;
    }

    tools[toolKey] = tool;

    // If the background-task manager has already initialized, register the
    // newly-added tool with its static registry so cross-process workers can
    // resolve dispatches for it. If init hasn't happened yet, the registry
    // will be populated wholesale in #ensureBackgroundTaskManager().
    if (this.#backgroundTaskManager) {
      this.#registerToolWithBackgroundManager(toolKey, tool);
    }
  }

  /**
   * Retrieves a specific processor by registration key.
   *
   * @throws {MastraError} When the specified processor is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   processors: {
   *     validator: validatorProcessor,
   *     transformer: transformerProcessor
   *   }
   * });
   *
   * const processor = mastra.getProcessor('validator');
   * ```
   */
  public getProcessor<TProcessorName extends keyof TProcessors>(name: TProcessorName): TProcessors[TProcessorName] {
    if (!this.#processors || !this.#processors[name]) {
      const error = new MastraError({
        id: 'MASTRA_GET_PROCESSOR_BY_NAME_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Processor with name ${String(name)} not found`,
        details: {
          status: 404,
          processorName: String(name),
          processors: Object.keys(this.#processors ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return this.#processors[name];
  }

  /**
   * Retrieves a specific processor by its ID.
   *
   * @throws {MastraError} When the specified processor is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   processors: {
   *     validator: validatorProcessor
   *   }
   * });
   *
   * const processor = mastra.getProcessorById('validator-processor-id');
   * ```
   */
  public getProcessorById<TProcessorName extends keyof TProcessors>(
    id: TProcessors[TProcessorName]['id'],
  ): TProcessors[TProcessorName] {
    const allProcessors = this.#processors;

    if (!allProcessors) {
      throw new MastraError({
        id: 'MASTRA_GET_PROCESSOR_BY_ID_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Processor with id ${id} not found`,
      });
    }

    // First try to find by internal ID
    for (const processor of Object.values(allProcessors)) {
      if (processor.id === id) {
        return processor as TProcessors[TProcessorName];
      }
    }

    // Fallback to searching by registration key
    const processorByKey = allProcessors[id];
    if (processorByKey) {
      return processorByKey as TProcessors[TProcessorName];
    }

    const error = new MastraError({
      id: 'MASTRA_GET_PROCESSOR_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Processor with id ${id} not found`,
      details: {
        status: 404,
        processorId: String(id),
        processors: Object.keys(allProcessors).join(', '),
      },
    });
    this.#logger?.trackException(error);
    throw error;
  }

  /**
   * Lists all configured processors.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   processors: {
   *     validator: validatorProcessor,
   *     transformer: transformerProcessor
   *   }
   * });
   *
   * const processors = mastra.listProcessors();
   * Object.entries(processors || {}).forEach(([name, processor]) => {
   *   console.log(`Processor "${name}":`, processor.id);
   * });
   * ```
   */
  public listProcessors(): TProcessors | undefined {
    return this.#processors;
  }

  /**
   * Adds a new processor to the Mastra instance.
   *
   * This method allows dynamic registration of processors after the Mastra instance
   * has been created.
   *
   * @throws {MastraError} When a processor with the same key already exists
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newProcessor = {
   *   id: 'text-processor',
   *   processInput: async (messages) => messages
   * };
   * mastra.addProcessor(newProcessor); // Uses processor.id as key
   * // or
   * mastra.addProcessor(newProcessor, 'customKey'); // Uses custom key
   * ```
   */
  public addProcessor<P extends Processor>(processor: P, key?: string): void {
    if (!processor) {
      throw createUndefinedPrimitiveError('processor', processor, key);
    }
    const processorKey = key || processor.id;
    const processors = this.#processors as Record<string, Processor>;
    if (processors[processorKey]) {
      return;
    }

    // Register Mastra with the processor if it supports it
    if (typeof processor.__registerMastra === 'function') {
      processor.__registerMastra(this);
    }

    processors[processorKey] = processor;
  }

  /**
   * Registers a processor configuration with agent context.
   * This tracks which agents use which processors with what configuration.
   *
   * @param processor - The processor instance
   * @param agentId - The ID of the agent that uses this processor
   * @param type - Whether this is an input or output processor
   */
  public addProcessorConfiguration(processor: Processor, agentId: string, type: 'input' | 'output'): void {
    const processorId = processor.id;
    if (!this.#processorConfigurations.has(processorId)) {
      this.#processorConfigurations.set(processorId, []);
    }
    const configs = this.#processorConfigurations.get(processorId)!;

    // Check if this exact configuration already exists
    const exists = configs.some(c => c.agentId === agentId && c.type === type);
    if (!exists) {
      configs.push({ processor, agentId, type });
    }
  }

  /**
   * Gets all processor configurations for a specific processor ID.
   *
   * @param processorId - The ID of the processor
   * @returns Array of configurations with agent context
   */
  public getProcessorConfigurations(
    processorId: string,
  ): Array<{ processor: Processor; agentId: string; type: 'input' | 'output' }> {
    return this.#processorConfigurations.get(processorId) || [];
  }

  /**
   * Gets all processor configurations.
   *
   * @returns Map of processor IDs to their configurations
   */
  public listProcessorConfigurations(): Map<
    string,
    Array<{ processor: Processor; agentId: string; type: 'input' | 'output' }>
  > {
    return this.#processorConfigurations;
  }

  /**
   * Retrieves a registered memory instance by its registration key.
   *
   * @throws {MastraError} When the memory instance with the specified key is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   memory: {
   *     chat: new Memory({ storage })
   *   }
   * });
   *
   * const chatMemory = mastra.getMemory('chat');
   * ```
   */
  public getMemory<TMemoryName extends keyof TMemory>(name: TMemoryName): TMemory[TMemoryName] {
    if (!this.#memory || !this.#memory[name]) {
      const error = new MastraError({
        id: 'MASTRA_GET_MEMORY_BY_KEY_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Memory with key ${String(name)} not found`,
        details: {
          status: 404,
          memoryKey: String(name),
          memory: Object.keys(this.#memory ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return this.#memory[name];
  }

  /**
   * Retrieves a registered memory instance by its ID.
   *
   * Searches through all registered memory instances and returns the one whose ID matches.
   *
   * @throws {MastraError} When no memory instance with the specified ID is found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   memory: {
   *     chat: new Memory({ id: 'chat-memory', storage })
   *   }
   * });
   *
   * const memory = mastra.getMemoryById('chat-memory');
   * ```
   */
  public getMemoryById(id: string): MastraMemory {
    const allMemory = this.#memory;
    if (allMemory) {
      for (const [, memory] of Object.entries(allMemory)) {
        if (memory.id === id) {
          return memory;
        }
      }
    }

    const error = new MastraError({
      id: 'MASTRA_GET_MEMORY_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Memory with id ${id} not found`,
      details: {
        status: 404,
        memoryId: id,
        availableIds: Object.values(allMemory ?? {})
          .map(m => m.id)
          .join(', '),
      },
    });
    this.#logger?.trackException(error);
    throw error;
  }

  /**
   * Returns all registered memory instances as a record keyed by their names.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   memory: {
   *     chat: new Memory({ storage }),
   *     longTerm: new Memory({ storage })
   *   }
   * });
   *
   * const allMemory = mastra.listMemory();
   * console.log(Object.keys(allMemory)); // ['chat', 'longTerm']
   * ```
   */
  public listMemory(): TMemory | undefined {
    return this.#memory;
  }

  /**
   * Adds a new memory instance to the Mastra instance.
   *
   * This method allows dynamic registration of memory instances after the Mastra instance
   * has been created.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const chatMemory = new Memory({
   *   id: 'chat-memory',
   *   storage: mastra.getStorage()
   * });
   * mastra.addMemory(chatMemory); // Uses memory.id as key
   * // or
   * mastra.addMemory(chatMemory, 'customKey'); // Uses custom key
   * ```
   */
  public addMemory<M extends MastraMemory>(memory: M, key?: string): void {
    if (!memory) {
      throw createUndefinedPrimitiveError('memory', memory, key);
    }
    const memoryKey = key || memory.id;
    const memoryRegistry = this.#memory as Record<string, MastraMemory>;
    if (memoryRegistry[memoryKey]) {
      return;
    }

    memory.__registerMastra(this);
    if (!memory.hasOwnStorage) {
      const storage = this.getStorage();
      if (storage) {
        memory.setStorage(storage);
      }
    }

    memoryRegistry[memoryKey] = memory;
  }

  /**
   * Returns all registered workflows as a record keyed by their IDs.
   *
   * @example Listing all workflows
   * ```typescript
   * const mastra = new Mastra({
   *   workflows: {
   *     dataProcessor: createWorkflow({...}).commit(),
   *     emailSender: createWorkflow({...}).commit(),
   *     reportGenerator: createWorkflow({...}).commit()
   *   }
   * });
   *
   * const allWorkflows = mastra.listWorkflows();
   * console.log(Object.keys(allWorkflows)); // ['dataProcessor', 'emailSender', 'reportGenerator']
   *
   * // Execute all workflows with sample data
   * for (const [id, workflow] of Object.entries(allWorkflows)) {
   *   console.log(`Workflow ${id}:`, workflow.name);
   *   // const result = await workflow.execute(sampleData);
   * }
   * ```
   */
  public listWorkflows(props: { serialized?: boolean } = {}): Record<string, Workflow> {
    const workflows = Object.fromEntries(
      Object.entries(this.#workflows).filter(([key]) => !this.#hiddenWorkflowKeys.has(key)),
    ) as Record<string, Workflow>;

    if (props.serialized) {
      return Object.entries(workflows).reduce((acc, [k, v]) => {
        return {
          ...acc,
          [k]: { name: v.name },
        };
      }, {});
    }
    return workflows;
  }

  /**
   * Adds a new workflow to the Mastra instance.
   *
   * This method allows dynamic registration of workflows after the Mastra instance
   * has been created. The workflow will be initialized with Mastra and primitives.
   *
   * @throws {MastraError} When a workflow with the same key already exists
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newWorkflow = createWorkflow({
   *   id: 'data-pipeline',
   *   name: 'Data Pipeline'
   * }).commit();
   * mastra.addWorkflow(newWorkflow); // Uses workflow.id as key
   * // or
   * mastra.addWorkflow(newWorkflow, 'customKey'); // Uses custom key
   * ```
   */
  public addWorkflow(workflow: AnyWorkflow, key?: string): void {
    if (!workflow) {
      throw createUndefinedPrimitiveError('workflow', workflow, key);
    }
    const workflowKey = key || workflow.id;
    const workflows = this.#workflows as Record<string, AnyWorkflow>;
    if (workflows[workflowKey]) {
      return;
    }

    // Note on schedules: a workflow declaring a `schedule` is auto-promoted to
    // the evented engine by the `createWorkflow` factory. We don't reject default-
    // engine workflows that happen to carry schedule configs — those would only
    // exist if a user constructed `Workflow` directly, in which case they've
    // explicitly opted out of the factory's promotion behavior and we trust them.
    const scheduleConfigs = collectWorkflowScheduleConfigs(workflow);
    const hasSchedule = scheduleConfigs.length > 0;

    // Initialize the workflow with Mastra and primitives
    workflow.__registerMastra(this);
    workflow.__registerPrimitives({
      logger: this.getLogger(),
      storage: this.getStorage(),
    });
    if (!workflow.committed) {
      workflow.commit();
    }
    workflows[workflowKey] = workflow;

    this.registerStaticWorkflowScorers(workflow);

    // If a schedule is declared, mark the flag and register into the
    // running scheduler worker (if already started).
    if (hasSchedule) {
      this.#hasScheduledWorkflow = true;
      const worker = this.#findSchedulerWorker();
      if (worker?.scheduler) {
        void (async () => {
          try {
            const schedulesStore = await this.#storage?.getStore('schedules');
            if (!schedulesStore) return;
            await this.registerDeclarativeSchedules(schedulesStore);
          } catch (error) {
            this.#logger?.error('Failed to register declarative schedule for workflow', {
              workflowId: workflow.id,
              error,
            });
          }
        })();
      }
      // If the worker doesn't exist yet (workers not started), schedules
      // will be registered when SchedulerWorker.init() runs.
    }
  }

  private registerStaticWorkflowScorers(workflow: AnyWorkflow): void {
    for (const step of Object.values(workflow.steps ?? {})) {
      const scorers = step.scorers;
      if (!scorers || typeof scorers === 'function') {
        continue;
      }

      for (const [, entry] of Object.entries(scorers)) {
        this.addScorer(entry.scorer, undefined, { source: 'code' });
      }
    }
  }

  /**
   * Sets the storage provider for the Mastra instance.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   *
   * // Set PostgreSQL storage
   * mastra.setStorage(new PostgresStore({
   *   connectionString: process.env.DATABASE_URL
   * }));
   *
   * // Now agents can use memory with the storage
   * const agent = new Agent({
   *   id: 'assistant',
   *   name: 'assistant',
   *   memory: new Memory({ storage: mastra.getStorage() })
   * });
   * ```
   */
  public setStorage(storage: MastraCompositeStore) {
    this.#storage = augmentWithInit(storage);
    this.#storage?.__registerMastra?.(this as unknown as Parameters<NonNullable<typeof storage.__registerMastra>>[0]);
    this.#ensureBackgroundTaskManager();
    // If storage was attached after construction, the SchedulerWorker
    // will pick it up when startWorkers() is called.
  }

  public setLogger({ logger }: { logger: TLogger }) {
    // Wrap the new logger in a DualLogger to maintain dual-write to loggerVNext
    const dualLogger = new DualLogger(logger, () => this.loggerVNext);
    this.#logger = dualLogger as unknown as TLogger;

    if (this.#agents) {
      Object.keys(this.#agents).forEach(key => {
        this.#agents?.[key]?.__setLogger(this.#logger);
      });
    }

    if (this.#deployer) {
      this.#deployer.__setLogger(this.#logger);
    }

    if (this.#tts) {
      Object.keys(this.#tts).forEach(key => {
        this.#tts?.[key]?.__setLogger(this.#logger);
      });
    }

    if (this.#storage) {
      this.#storage.__setLogger(this.#logger);
    }

    if (this.#vectors) {
      Object.keys(this.#vectors).forEach(key => {
        this.#vectors?.[key]?.__setLogger(this.#logger);
      });
    }

    if (this.#mcpServers) {
      Object.keys(this.#mcpServers).forEach(key => {
        this.#mcpServers?.[key]?.__setLogger(this.#logger);
      });
    }

    if (this.#workflows) {
      Object.keys(this.#workflows).forEach(key => {
        this.#workflows?.[key]?.__setLogger(this.#logger);
      });
    }

    if (this.#serverAdapter) {
      this.#serverAdapter.__setLogger(this.#logger);
    }

    if (this.#workspace) {
      this.#workspace.__setLogger(this.#logger);
    }

    if (this.#memory) {
      Object.keys(this.#memory).forEach(key => {
        this.#memory?.[key]?.__setLogger(this.#logger);
      });
    }

    // Pass the raw logger (not the DualLogger) to observability to avoid circular forwarding
    this.#observability.setLogger({ logger });
  }

  /**
   * Gets all registered text-to-speech (TTS) providers.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   tts: {
   *     openai: new OpenAITTS({
   *       apiKey: process.env.OPENAI_API_KEY,
   *       voice: 'alloy'
   *     })
   *   }
   * });
   *
   * const ttsProviders = mastra.getTTS();
   * const openaiTTS = ttsProviders?.openai;
   * if (openaiTTS) {
   *   const audioBuffer = await openaiTTS.synthesize('Hello, world!');
   * }
   * ```
   */
  public getTTS() {
    return this.#tts;
  }

  /**
   * Gets the currently configured logger instance.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   logger: new PinoLogger({
   *     name: 'MyApp',
   *     level: 'info'
   *   })
   * });
   *
   * const logger = mastra.getLogger();
   * logger.info('Application started');
   * logger.error('An error occurred', { error: 'details' });
   * ```
   */
  public getLogger() {
    return this.#logger;
  }

  /**
   * Gets the currently configured storage provider.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   storage: new LibSQLStore({ id: 'mastra-storage', url: 'file:./data.db' })
   * });
   *
   * // Use the storage in agent memory
   * const agent = new Agent({
   *   id: 'assistant',
   *   name: 'assistant',
   *   memory: new Memory({
   *     storage: mastra.getStorage()
   *   })
   * });
   * ```
   */
  public getStorage() {
    return this.#storage;
  }

  get observability(): ObservabilityEntrypoint {
    return this.#observability;
  }

  /**
   * Structured logging API for observability.
   * Logs emitted via this API will not have trace correlation when used outside a span.
   * Use for startup logs, background jobs, or other non-traced scenarios.
   *
   * Note: For the infrastructure logger (IMastraLogger), use getLogger() instead.
   */
  get loggerVNext(): LoggerContext {
    return this.#observability.getDefaultInstance()?.getLoggerContext?.() ?? noOpLoggerContext;
  }

  /**
   * Direct metrics API for use outside trace context.
   * Metrics emitted via this API will not have auto correlation or cost context from spans.
   * Use for background jobs, startup metrics, or other non-traced scenarios.
   */
  get metrics(): MetricsContext {
    return this.#observability.getDefaultInstance()?.getMetricsContext?.() ?? noOpMetricsContext;
  }

  public getServerMiddleware() {
    return this.#serverMiddleware;
  }

  public getServerCache() {
    return this.#serverCache;
  }

  public setServerMiddleware(serverMiddleware: Middleware | Middleware[]) {
    if (typeof serverMiddleware === 'function') {
      this.#serverMiddleware = [
        {
          handler: serverMiddleware,
          path: '/api/*',
        },
      ];
      return;
    }

    if (!Array.isArray(serverMiddleware)) {
      const error = new MastraError({
        id: 'MASTRA_SET_SERVER_MIDDLEWARE_INVALID_TYPE',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Invalid middleware: expected a function or array, received ${typeof serverMiddleware}`,
      });
      this.#logger?.trackException(error);
      throw error;
    }

    this.#serverMiddleware = serverMiddleware.map(m => {
      if (typeof m === 'function') {
        return {
          handler: m,
          path: '/api/*',
        };
      }
      return {
        handler: m.handler,
        path: m.path || '/api/*',
      };
    });
  }

  public getServer() {
    return this.#server;
  }

  /**
   * Sets the server adapter for this Mastra instance.
   *
   * The server adapter provides access to the underlying server app (e.g., Hono, Express)
   * and allows users to call routes directly via `app.fetch()` instead of making HTTP requests.
   *
   * This is typically called by `createHonoServer` or similar factory functions during
   * server initialization.
   *
   * @param adapter - The server adapter instance (e.g., MastraServer from @mastra/hono or @mastra/express)
   *
   * @example
   * ```typescript
   * const app = new Hono();
   * const adapter = new MastraServer({ app, mastra });
   * mastra.setMastraServer(adapter);
   * ```
   */
  public setMastraServer(adapter: MastraServerBase): void {
    if (this.#serverAdapter) {
      this.#logger?.debug(
        'Replacing existing server adapter. Only one adapter should be registered per Mastra instance.',
      );
    }
    this.#serverAdapter = adapter;
    // Inject the logger into the adapter
    if (this.#logger) {
      adapter.__setLogger(this.#logger);
    }
  }

  /**
   * Gets the server adapter for this Mastra instance.
   *
   * @returns The server adapter, or undefined if not set
   *
   * @example
   * ```typescript
   * const adapter = mastra.getMastraServer();
   * if (adapter) {
   *   const app = adapter.getApp<Hono>();
   * }
   * ```
   */
  public getMastraServer(): MastraServerBase | undefined {
    return this.#serverAdapter;
  }

  /**
   * Gets the server app from the server adapter.
   *
   * This is a convenience method that calls `getMastraServer()?.getApp<T>()`.
   * Use this to access the underlying server framework's app instance (e.g., Hono, Express)
   * for direct operations like calling routes via `app.fetch()`.
   *
   * @template T - The expected type of the app (e.g., Hono, Express Application)
   * @returns The server app, or undefined if no adapter is set
   *
   * @example
   * ```typescript
   * // After createHonoServer() is called:
   * const app = mastra.getServerApp<Hono>();
   *
   * // Call routes directly without HTTP overhead
   * const response = await app?.fetch(new Request('http://localhost/health'));
   * const data = await response?.json();
   * ```
   */
  public getServerApp<T = unknown>(): T | undefined {
    return this.#serverAdapter?.getApp<T>();
  }

  public getBundlerConfig() {
    return this.#bundler;
  }

  public async listLogsByRunId({
    runId,
    transportId,
    fromDate,
    toDate,
    logLevel,
    filters,
    page,
    perPage,
  }: {
    runId: string;
    transportId: string;
    fromDate?: Date;
    toDate?: Date;
    logLevel?: LogLevel;
    filters?: Record<string, any>;
    page?: number;
    perPage?: number;
  }) {
    if (!transportId) {
      const error = new MastraError({
        id: 'MASTRA_LIST_LOGS_BY_RUN_ID_MISSING_TRANSPORT',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: 'Transport ID is required',
        details: {
          runId,
          transportId,
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    if (!this.#logger?.listLogsByRunId) {
      const error = new MastraError({
        id: 'MASTRA_GET_LOGS_BY_RUN_ID_LOGGER_NOT_CONFIGURED',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.SYSTEM,
        text: 'Logger is not configured or does not support listLogsByRunId operation',
        details: {
          runId,
          transportId,
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    return await this.#logger.listLogsByRunId({
      runId,
      transportId,
      fromDate,
      toDate,
      logLevel,
      filters,
      page,
      perPage,
    });
  }

  public async listLogs(
    transportId: string,
    params?: {
      fromDate?: Date;
      toDate?: Date;
      logLevel?: LogLevel;
      filters?: Record<string, any>;
      page?: number;
      perPage?: number;
    },
  ) {
    if (!transportId) {
      const error = new MastraError({
        id: 'MASTRA_GET_LOGS_MISSING_TRANSPORT',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: 'Transport ID is required',
        details: {
          transportId,
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    if (!this.#logger) {
      const error = new MastraError({
        id: 'MASTRA_GET_LOGS_LOGGER_NOT_CONFIGURED',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.SYSTEM,
        text: 'Logger is not set',
        details: {
          transportId,
        },
      });
      throw error;
    }

    return await this.#logger.listLogs(transportId, params);
  }

  /**
   * Gets all registered Model Context Protocol (MCP) server instances.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   mcpServers: {
   *     filesystem: new FileSystemMCPServer({
   *       rootPath: '/app/data'
   *     })
   *   }
   * });
   *
   * const mcpServers = mastra.getMCPServers();
   * if (mcpServers) {
   *   const fsServer = mcpServers.filesystem;
   *   const tools = await fsServer.listTools();
   * }
   * ```
   */
  public listMCPServers(): Record<string, MCPServerBase> | undefined {
    return this.#mcpServers;
  }

  /**
   * Adds a new MCP server to the Mastra instance.
   *
   * This method allows dynamic registration of MCP servers after the Mastra instance
   * has been created. The server will be initialized with ID, Mastra instance, and logger.
   *
   * @throws {MastraError} When an MCP server with the same key already exists
   *
   * @example
   * ```typescript
   * const mastra = new Mastra();
   * const newServer = new FileSystemMCPServer({
   *   rootPath: '/data'
   * });
   * mastra.addMCPServer(newServer); // Uses server.id as key
   * // or
   * mastra.addMCPServer(newServer, 'customKey'); // Uses custom key
   * ```
   */
  public addMCPServer<M extends MCPServerBase>(server: M, key?: string): void {
    if (!server) {
      throw createUndefinedPrimitiveError('mcp-server', server, key);
    }
    // If a key is provided, try to set it as the ID
    // The setId method will only update if the ID wasn't explicitly set by the user
    if (key) {
      server.setId(key);
    }

    // Now resolve the ID after potentially setting it
    const resolvedId = server.id;
    if (!resolvedId) {
      const error = new MastraError({
        id: 'MASTRA_ADD_MCP_SERVER_MISSING_ID',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: 'MCP server must expose an id or be registered under one',
        details: { status: 400 },
      });
      this.#logger?.trackException(error);
      throw error;
    }

    const serverKey = key ?? resolvedId;
    const servers = this.#mcpServers as Record<string, MCPServerBase>;
    if (servers[serverKey]) {
      return;
    }

    // Initialize the server
    server.__registerMastra(this);
    server.__setLogger(this.getLogger());
    servers[serverKey] = server;
  }

  /**
   * Retrieves a specific MCP server instance by registration key.
   *
   * @throws {MastraError} When the specified MCP server is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   mcpServers: {
   *     filesystem: new FileSystemMCPServer({...})
   *   }
   * });
   *
   * const fsServer = mastra.getMCPServer('filesystem');
   * const tools = await fsServer.listTools();
   * ```
   */
  public getMCPServer<TMCPServerName extends keyof TMCPServers>(
    name: TMCPServerName,
  ): TMCPServers[TMCPServerName] | undefined {
    if (!this.#mcpServers || !this.#mcpServers[name]) {
      this.#logger?.debug(`MCP server with name ${String(name)} not found`);
      return undefined as TMCPServers[TMCPServerName] | undefined;
    }
    return this.#mcpServers[name];
  }

  /**
   * Retrieves a specific Model Context Protocol (MCP) server instance by its logical ID.
   *
   * This method searches for an MCP server using its logical ID. If a version is specified,
   * it returns the exact version match. If no version is provided, it returns the server
   * with the most recent release date.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   mcpServers: {
   *     filesystem: new FileSystemMCPServer({
   *       id: 'fs-server',
   *       version: '1.0.0',
   *       rootPath: '/app/data'
   *     })
   *   }
   * });
   *
   * const fsServer = mastra.getMCPServerById('fs-server');
   * if (fsServer) {
   *   const tools = await fsServer.listTools();
   * }
   * ```
   */
  public getMCPServerById<TMCPServerName extends keyof TMCPServers>(
    serverId: TMCPServers[TMCPServerName]['id'],
    version?: string,
  ): TMCPServers[TMCPServerName] | undefined {
    if (!this.#mcpServers) {
      return undefined;
    }

    const allRegisteredServers = Object.values(this.#mcpServers || {});

    const matchingLogicalIdServers = allRegisteredServers.filter(server => server.id === serverId);

    if (matchingLogicalIdServers.length === 0) {
      this.#logger?.debug(`No MCP servers found with logical ID: ${serverId}`);
      return undefined;
    }

    if (version) {
      const specificVersionServer = matchingLogicalIdServers.find(server => server.version === version);
      if (!specificVersionServer) {
        this.#logger?.debug(`MCP server with logical ID '${serverId}' found, but not version '${version}'.`);
      }
      return specificVersionServer as TMCPServers[TMCPServerName] | undefined;
    } else {
      // No version specified, find the one with the most recent releaseDate
      if (matchingLogicalIdServers.length === 1) {
        return matchingLogicalIdServers[0] as TMCPServers[TMCPServerName];
      }

      matchingLogicalIdServers.sort((a, b) => {
        // Ensure releaseDate exists and is a string before creating a Date object
        const dateAVal = a.releaseDate && typeof a.releaseDate === 'string' ? new Date(a.releaseDate).getTime() : NaN;
        const dateBVal = b.releaseDate && typeof b.releaseDate === 'string' ? new Date(b.releaseDate).getTime() : NaN;

        if (isNaN(dateAVal) && isNaN(dateBVal)) return 0;
        if (isNaN(dateAVal)) return 1; // Treat invalid/missing dates as older
        if (isNaN(dateBVal)) return -1; // Treat invalid/missing dates as older

        return dateBVal - dateAVal; // Sorts in descending order of time (latest first)
      });

      // After sorting, the first element should be the latest if its date is valid
      if (matchingLogicalIdServers.length > 0) {
        const latestServer = matchingLogicalIdServers[0];
        if (
          latestServer &&
          latestServer.releaseDate &&
          typeof latestServer.releaseDate === 'string' &&
          !isNaN(new Date(latestServer.releaseDate).getTime())
        ) {
          return latestServer as TMCPServers[TMCPServerName];
        }
      }
      this.#logger?.warn(
        `Could not determine the latest server for logical ID '${serverId}' due to invalid or missing release dates, or no servers left after filtering.`,
      );
      return undefined;
    }
  }

  public async addTopicListener(topic: string, listener: (event: any) => Promise<void>) {
    await this.#pubsub.subscribe(topic, listener);
  }

  public async removeTopicListener(topic: string, listener: (event: any) => Promise<void>) {
    await this.#pubsub.unsubscribe(topic, listener);
  }

  /**
   * Process a single workflow event. Shared entry point used by:
   * - pull-mode workers (OrchestrationWorker)
   * - in-process push pubsubs (EventEmitterPubSub) wired during startWorkers()
   * - HTTP push delivered to `POST /api/workers/events`
   *
   * Returns `{ ok: true }` on success; the caller should ack/return 2xx.
   * Returns `{ ok: false, retry: true }` on transient failure; the caller
   * should nack/return 5xx so the broker retries.
   */
  public async handleWorkflowEvent(event: Event): Promise<{ ok: true } | { ok: false; retry: boolean }> {
    if (!this.#workflowEventProcessor) {
      this.#workflowEventProcessor = new WorkflowEventProcessor({ mastra: this });
    }
    return this.#workflowEventProcessor.handle(event);
  }

  /**
   * Initialize and start workers. If `name` is provided, starts only
   * that worker. Otherwise starts all registered workers and subscribes
   * user-defined event listeners.
   */
  public async startWorkers(name?: string): Promise<void> {
    // Lazily inject the SchedulerWorker if the scheduler should be enabled
    // and no scheduler worker is registered yet. This runs after all
    // workflows have been registered (unlike the constructor's default-workers
    // block), so #hasScheduledWorkflow is accurate.
    if (!name && this.#shouldEnableScheduler() && this.#storage && !this.#findSchedulerWorker()) {
      const sw = new SchedulerWorker(this.#schedulerConfig);
      sw.__registerMastra(this);
      this.#workers.push(sw);
    }

    const deps: WorkerDeps = {
      pubsub: this.#pubsub,
      storage: this.#storage!,
      logger: this.#logger as unknown as IMastraLogger,
      mastra: this,
    };

    let targets: MastraWorker[];
    if (name) {
      targets = this.#workers.filter(w => w.name === name);
      if (targets.length === 0) {
        throw new Error(`Worker "${name}" not found. Available: ${this.#workers.map(w => w.name).join(', ')}`);
      }
    } else if (this.#workerFilter) {
      targets = this.#workers.filter(w => this.#workerFilter!.has(w.name));
      if (targets.length === 0) {
        this.#logger?.warn?.(
          `MASTRA_WORKERS=${[...this.#workerFilter].join(',')} did not match any registered workers (have: ${this.#workers.map(w => w.name).join(', ')})`,
        );
      }
    } else {
      targets = this.#workers;
    }

    for (const worker of targets) {
      await worker.init(deps);
      await worker.start();
    }

    // For push-mode pubsubs (e.g. EventEmitterPubSub) there is no
    // OrchestrationWorker pulling events — wire handleWorkflowEvent directly
    // to the pubsub so workflow events still get processed in-process.
    if (!name) {
      const modes = this.#pubsub.supportedModes ?? ['pull'];
      const pushOnly = modes.includes('push') && !modes.includes('pull');
      if (pushOnly && !this.#pushSubscription) {
        const cb: EventCallback = (event, ack) => {
          void this.handleWorkflowEvent(event)
            .then(result => {
              if (result.ok && ack) {
                return ack().catch(err =>
                  this.#logger?.error?.('Error acking workflow event in push subscription', err),
                );
              }
              // Push transports without nack semantics (EventEmitter) treat
              // a non-ack as a failure signal; we already logged inside handle().
            })
            .catch(err => this.#logger?.error?.('Unhandled error in workflow event push subscription', err));
        };
        await this.#pubsub.subscribe('workflows', cb);
        this.#pushSubscription = { topic: 'workflows', cb };
      }
    }

    // Subscribe user-defined event listeners (non-workflow topics, or legacy inline WEP)
    // Only when starting all workers (not when targeting a specific one).
    // Idempotent: skip pairs we've already subscribed.
    if (!name) {
      for (const topic in this.#events) {
        if (!this.#events[topic]) {
          continue;
        }

        const listeners = Array.isArray(this.#events[topic]) ? this.#events[topic] : [this.#events[topic]];
        for (const listener of listeners) {
          const alreadySubscribed = this.#userEventSubscriptions.some(
            sub => sub.topic === topic && sub.cb === listener,
          );
          if (alreadySubscribed) continue;
          await this.#pubsub.subscribe(topic, listener);
          this.#userEventSubscriptions.push({ topic, cb: listener });
        }
      }
    }
  }

  /**
   * Stop all running workers and unsubscribe event listeners.
   */
  public async stopWorkers(): Promise<void> {
    // Stop registered workers in reverse order
    for (const worker of [...this.#workers].reverse()) {
      if (worker.isRunning) {
        await worker.stop();
      }
    }

    // Tear down the in-process push subscription wired during startWorkers().
    if (this.#pushSubscription) {
      await this.#pubsub.unsubscribe(this.#pushSubscription.topic, this.#pushSubscription.cb);
      this.#pushSubscription = undefined;
    }

    // Unsubscribe only the (topic, listener) pairs we actually registered in
    // startWorkers() — keeps stopWorkers() symmetric with startWorkers() and
    // avoids unsubscribing listeners that startWorkers never owned.
    for (const { topic, cb } of this.#userEventSubscriptions) {
      await this.#pubsub.unsubscribe(topic, cb);
    }
    this.#userEventSubscriptions = [];

    await this.#pubsub.flush();
  }

  /**
   * @deprecated Use {@link Mastra.startWorkers} instead. Will be removed in a
   * future release.
   */
  public async startEventEngine(name?: string): Promise<void> {
    return this.startWorkers(name);
  }

  /**
   * @deprecated Use {@link Mastra.stopWorkers} instead. Will be removed in a
   * future release.
   */
  public async stopEventEngine(): Promise<void> {
    return this.stopWorkers();
  }

  /**
   * Retrieves a registered gateway by its key.
   *
   * @throws {MastraError} When the gateway with the specified key is not found
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   gateways: {
   *     myGateway: new CustomGateway()
   *   }
   * });
   *
   * const gateway = mastra.getGateway('myGateway');
   * ```
   */
  public getGateway(key: string): MastraModelGateway {
    const gateway = this.#gateways?.[key];
    if (!gateway) {
      const error = new MastraError({
        id: 'MASTRA_GET_GATEWAY_BY_KEY_NOT_FOUND',
        domain: ErrorDomain.MASTRA,
        category: ErrorCategory.USER,
        text: `Gateway with key ${key} not found`,
        details: {
          status: 404,
          gatewayKey: key,
          gateways: Object.keys(this.#gateways ?? {}).join(', '),
        },
      });
      this.#logger?.trackException(error);
      throw error;
    }
    return gateway;
  }

  /**
   * Retrieves a registered gateway by its ID.
   *
   * Searches through all registered gateways and returns the one whose ID matches.
   * If a gateway doesn't have an explicit ID, its name is used as the ID.
   *
   * @throws {MastraError} When no gateway with the specified ID is found
   *
   * @example
   * ```typescript
   * class CustomGateway extends MastraModelGateway {
   *   readonly id = 'custom-gateway-v1';
   *   readonly name = 'Custom Gateway';
   *   // ...
   * }
   *
   * const mastra = new Mastra({
   *   gateways: {
   *     myGateway: new CustomGateway()
   *   }
   * });
   *
   * const gateway = mastra.getGatewayById('custom-gateway-v1');
   * ```
   */
  public getGatewayById(id: string): MastraModelGateway {
    const gateways = this.#gateways ?? {};
    for (const gateway of Object.values(gateways)) {
      if (gateway.getId() === id) {
        return gateway;
      }
    }

    const error = new MastraError({
      id: 'MASTRA_GET_GATEWAY_BY_ID_NOT_FOUND',
      domain: ErrorDomain.MASTRA,
      category: ErrorCategory.USER,
      text: `Gateway with ID ${id} not found`,
      details: {
        status: 404,
        gatewayId: id,
        availableIds: Object.values(gateways)
          .map(g => g.getId())
          .join(', '),
      },
    });
    this.#logger?.trackException(error);
    throw error;
  }

  /**
   * Returns all registered gateways as a record keyed by their names.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   gateways: {
   *     netlify: new NetlifyGateway(),
   *     custom: new CustomGateway()
   *   }
   * });
   *
   * const allGateways = mastra.listGateways();
   * console.log(Object.keys(allGateways)); // ['netlify', 'custom']
   * ```
   */
  public listGateways(): Record<string, MastraModelGateway> | undefined {
    return this.#gateways;
  }

  /**
   * Adds a new gateway to the Mastra instance.
   *
   * This method allows dynamic registration of gateways after the Mastra instance
   * has been created. Gateways enable access to LLM providers through custom
   * authentication and routing logic.
   *
   * If no key is provided, the gateway's ID (or name if no ID is set) will be used as the key.
   *
   * @example
   * ```typescript
   * import { MastraModelGateway } from '@mastra/core';
   *
   * class CustomGateway extends MastraModelGateway {
   *   readonly id = 'custom-gateway-v1';  // Optional, defaults to name
   *   readonly name = 'custom';
   *   readonly prefix = 'custom';
   *
   *   async fetchProviders() {
   *     return {
   *       myProvider: {
   *         name: 'My Provider',
   *         models: ['model-1', 'model-2'],
   *         apiKeyEnvVar: 'MY_API_KEY',
   *         gateway: 'custom'
   *       }
   *     };
   *   }
   *
   *   buildUrl(modelId: string) {
   *     return 'https://api.myprovider.com/v1';
   *   }
   *
   *   async getApiKey(modelId: string) {
   *     return process.env.MY_API_KEY || '';
   *   }
   *
   *   async resolveLanguageModel({ modelId, providerId, apiKey }) {
   *     const baseURL = this.buildUrl(`${providerId}/${modelId}`);
   *     return createOpenAICompatible({
   *       name: providerId,
   *       apiKey,
   *       baseURL,
   *       supportsStructuredOutputs: true,
   *     }).chatModel(modelId);
   *   }
   * }
   *
   * const mastra = new Mastra();
   * const newGateway = new CustomGateway();
   * mastra.addGateway(newGateway); // Uses gateway.getId() as key (gateway.id)
   * // or
   * mastra.addGateway(newGateway, 'customKey'); // Uses custom key
   * ```
   */
  public addGateway(gateway: MastraModelGateway, key?: string): void {
    if (!gateway) {
      throw createUndefinedPrimitiveError('gateway', gateway, key);
    }
    const gatewayKey = key || gateway.getId();
    const gateways = this.#gateways as Record<string, MastraModelGateway>;
    if (gateways[gatewayKey]) {
      return;
    }

    gateways[gatewayKey] = gateway;

    // Register custom gateways with the registry for type generation
    this.#syncGatewayRegistry();
  }

  /**
   * Sync custom gateways with the GatewayRegistry for type generation
   * @private
   */
  #syncGatewayRegistry(): void {
    try {
      // Only sync in dev mode (when MASTRA_DEV is set)
      if (process.env.MASTRA_DEV !== 'true' && process.env.MASTRA_DEV !== '1') {
        return;
      }

      // Trigger sync immediately (non-blocking, but logs progress)
      import('../llm/model/provider-registry.js')
        .then(async ({ GatewayRegistry }) => {
          const registry = GatewayRegistry.getInstance();
          const customGateways = Object.values(this.#gateways || {});
          registry.registerCustomGateways(customGateways);

          // Log that we're syncing
          const logger = this.getLogger();
          logger.info('🔄 Syncing custom gateway types...');

          // Trigger a sync to regenerate types
          await registry.syncGateways(true);

          logger.info('✅ Custom gateway types synced! Restart your TypeScript server to see autocomplete.');
        })
        .catch(err => {
          const logger = this.getLogger();
          logger.debug('Gateway registry sync skipped:', err);
        });
    } catch (err) {
      // Silent fail - this is a dev-only feature
      const logger = this.getLogger();
      logger.debug('Gateway registry sync failed:', err);
    }
  }

  /**
   * Gracefully shuts down the Mastra instance and cleans up all resources.
   *
   * This method performs a clean shutdown of all Mastra components, including:
   * - tracing registry and all tracing instances
   * - Event engine and pub/sub system
   * - All registered components and their resources
   *
   * It's important to call this method when your application is shutting down
   * to ensure proper cleanup and prevent resource leaks.
   *
   * @example
   * ```typescript
   * const mastra = new Mastra({
   *   agents: { myAgent },
   *   workflows: { myWorkflow }
   * });
   *
   * // Graceful shutdown on SIGINT
   * process.on('SIGINT', async () => {
   *   await mastra.shutdown();
   *   process.exit(0);
   * });
   * ```
   */
  async shutdown(): Promise<void> {
    // SchedulerWorker is stopped as part of stopWorkers().
    await this.stopWorkers();
    // Close storage to release OS file handles (critical on Windows: open WAL/shm
    // handles cause EBUSY when callers try to fs.rm the storage dir after shutdown).
    if (this.#storage?.close) {
      await this.#storage.close();
    }
    // Shutdown observability registry, exporters, etc...
    await this.#observability.shutdown();

    this.#logger?.info('Mastra shutdown completed');
  }

  // This method is only used internally for server hnadlers that require temporary persistence
  public get serverCache() {
    return this.#serverCache;
  }
}
